<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref, nextTick, watch, defineAsyncComponent } from 'vue'
import type { VehicleEnrichment } from '~/types/vehicle-enrichment'
import { useVehiclesStore } from '~/stores/vehicles'
import { useVehicleStore } from '~/stores/vehicleStore'
import { useANCAPStore } from '~/stores/ancap'
import { useSiteStore } from '~/stores/site'
import { storeToRefs } from 'pinia'
import { 
  ChevronRight, 
  Eye, 
  Gauge, 
  Car, 
  Fuel, 
  Settings, 
  MessageSquare, 
  ChevronDown, 
  ChevronUp, 
  Calendar, 
  Phone, 
  MessageCircle, 
  Calculator, 
  HelpCircle, 
  Star, 
  Clock, 
  Check, 
  Shield, 
  Award, 
  Wrench, 
  ShieldCheck, 
  Loader2,
  TrendingDown,
  TrendingUp,
  Bot,
  FileText,
  FileCheck,
  FileSignature,
  Users,
  User,
  MapPin,
  DollarSign,
  BarChart,
  Navigation
} from 'lucide-vue-next'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '~/layers/base/components/ui/tabs'

// Lazy-load heavier components to improve initial page performance
const PhotoCarouselSlide = defineAsyncComponent(() => import('~/layers/base/components/PhotoCarouselSlide.vue'))
const DealerMapAndHours = defineAsyncComponent(() => import('~/layers/base/components/DealerMapAndHours.vue'))
const VehicleANCAPRating = defineAsyncComponent(() => import('~/app/components/VehicleANCAPRating.vue'))
const VehicleActionDialog = defineAsyncComponent(() => import('~/app/components/VehicleActionDialog.vue'))
const VehicleAIChatDialog = defineAsyncComponent(() => import('~/app/components/VehicleAIChatDialog.vue'))

// Define Vehicle type locally to avoid import issues
interface Vehicle {
  id: string
  stockid?: string
  slug?: string
  title?: string
  description?: string
  price?: number | string
  kms?: number
  mileage?: number
  thumb?: string
  photos?: string[]
  image?: string
  make?: {
    value?: string[]
    displayValue?: string[]
  }
  model?: {
    value?: string[]
    displayValue?: string[]
  }
  badge?: {
    value?: string[]
    displayValue?: string[]
  }
  condition?: {
    value?: string[]
    displayValue?: string[]
  }
  body?: {
    value?: string[]
    displayValue?: string[]
  }
  transmission?: {
    value?: string[]
    displayValue?: string[]
  }
  fuel?: {
    value?: string[]
    displayValue?: string[]
  }
  seats?: {
    value?: string[]
    displayValue?: string[]
  }
  doors?: {
    value?: string[]
    displayValue?: string[]
  }
  drivetrain?: {
    value?: string[]
    displayValue?: string[]
  }
  engine?: {
    value?: string[]
    displayValue?: string[]
  }
  year?: {
    value?: string[]
    displayValue?: string[]
  } | number
  viewCount?: number
  previousOwners?: number
  address?: {
    line1?: string
    state?: string
    suburb?: string
    postcode?: string
  }
  sellername?: string
  [key: string]: any
}

// Get route parameters
const route = useRoute()
const router = useRouter()
const vehicleId = route.params.id as string

// Store instances - will be initialized in useAsyncData
let vehiclesStore: any
let vehicleStore: any
let ancapStore: any
let siteStore: any

// Reactive refs that will be populated after store initialization
const departments = ref({})
const isReady = ref(false)
const phone = ref('')

// Function to sync reactive refs with store data
const syncStoreRefs = () => {
  if (siteStore) {
    try {
      // Direct access to store properties is safer than storeToRefs for this use case
      departments.value = siteStore.departments || {}
      isReady.value = siteStore.isReady || false
      phone.value = siteStore.phone || ''
    } catch (error) {
      console.warn('[VehicleDetail] Error syncing store refs:', error)
    }
  }
}

// Reactive state
const selectedImage = ref('')
// Track client mount to coordinate SSR-safe rendering and hydration-sensitive values
const isClientMounted = ref(false)

// Default tab value from URL hash or fallback to features
const defaultTabValue = computed(() => {
  if (process.client) {
    const hash = route.hash.replace('#', '')
    if (['features', 'overview', 'specifications', 'history'].includes(hash)) {
      return hash
    }
  }
  return 'features'
})
const inquiry = ref({
  name: '',
  phone: '',
  email: '',
  message: '',
})
const submittingInquiry = ref(false)
const isLoading = ref(true)
const error = ref<string | null>(null)
const showFullDescription = ref(false)
const showFullAIDescription = ref(false)

// Vehicle data from store
const vehicle = ref<Vehicle | null>(null)

// Enrichment data
const enrichmentData = ref<VehicleEnrichment | null>(null)
const isLoadingEnrichment = ref(false)
const enrichmentError = ref<string | null>(null)

// Market intelligence data
const marketIntelligence = ref<any>(null)
const isLoadingMarketIntelligence = ref(false)
const marketIntelligenceError = ref<string | null>(null)

// Keep detail view in sync with live store updates
const storeVehicleRef = computed<Vehicle | null>(() => {
  try {
    if (!vehiclesStore || !vehicleId) return null
    const match = vehiclesStore.vehicles?.find((v: any) => v?.id === vehicleId || v?.stockid === vehicleId)
    return match || null
  } catch (e) {
    return null
  }
})

watch(storeVehicleRef, (updated) => {
  if (updated) {
    vehicle.value = updated
  }
}, { immediate: false })

// Computed properties for vehicle data with safe access
const vehicleYear = computed(() => {
  if (!vehicle.value) return ''
  if (typeof vehicle.value.year === 'number') return vehicle.value.year.toString()
  return vehicle.value.year?.value?.[0] || ''
})

const vehicleMake = computed(() => {
  return vehicle.value?.make?.displayValue?.[0] || vehicle.value?.make?.value?.[0] || ''
})

// Canonical slug value for make (used for filter query params)
const vehicleMakeSlug = computed(() => {
  return vehicle.value?.make?.value?.[0] || ''
})

const vehicleModel = computed(() => {
  return vehicle.value?.model?.displayValue?.[0] || vehicle.value?.model?.value?.[0] || ''
})

const vehicleBadge = computed(() => {
  return vehicle.value?.badge?.displayValue?.[0] || vehicle.value?.badge?.value?.[0] || ''
})

const vehicleCondition = computed(() => {
  return vehicle.value?.condition?.displayValue?.[0] || vehicle.value?.condition?.value?.[0] || 'Excellent'
})

const vehicleEngine = computed(() => {
  return vehicle.value?.engine?.displayValue?.[0] || vehicle.value?.engine?.value?.[0] || ''
})

const vehicleTransmission = computed(() => {
  return vehicle.value?.transmission?.displayValue?.[0] || vehicle.value?.transmission?.value?.[0] || ''
})

const vehicleFuel = computed(() => {
  return vehicle.value?.fuel?.displayValue?.[0] || vehicle.value?.fuel?.value?.[0] || ''
})

const vehicleBody = computed(() => {
  return vehicle.value?.body?.displayValue?.[0] || vehicle.value?.body?.value?.[0] || ''
})

const vehicleSeats = computed(() => {
  return vehicle.value?.seats?.displayValue?.[0] || vehicle.value?.seats?.value?.[0] || ''
})

const vehicleDoors = computed(() => {
  return vehicle.value?.doors?.displayValue?.[0] || vehicle.value?.doors?.value?.[0] || ''
})

const vehicleDrivetrain = computed(() => {
  return vehicle.value?.drivetrain?.displayValue?.[0] || vehicle.value?.drivetrain?.value?.[0] || ''
})

const vehicleImages = computed(() => {
  if (!vehicle.value) return []
  return vehicle.value.photos || (vehicle.value.image ? [vehicle.value.image] : [])
})

const vehiclePrice = computed(() => {
  return Number(vehicle.value?.price || 0)
})

const vehicleKms = computed(() => {
  return Number(vehicle.value?.kms || vehicle.value?.mileage || 0)
})

const vehicleWeeklyPayment = computed(() => {
  if (!vehiclePrice.value) return 0
  // Simple weekly payment calculation - you can import the actual function if available
  const monthlyRate = 0.06 / 12
  const months = 60
  const loanAmount = vehiclePrice.value * 0.95 // 5% down
  const monthlyPayment = (loanAmount * monthlyRate * (1 + monthlyRate) ** months) / ((1 + monthlyRate) ** months - 1)
  return Math.round(monthlyPayment / 4.33) // Convert monthly to weekly
})

const vehicleViewCount = computed(() => {
  return vehicle.value?.viewCount || 0
})

const vehicleDescription = computed(() => {
  const description = vehicle.value?.description || vehicle.value?.Comments || ''
  // Normalize whitespace to prevent hydration mismatches
  return description.replace(/\s+/g, ' ').trim()
})

// Memoized formatted descriptions to avoid repeated heavy regex processing
const formattedDealerDescription = computed(() => formatDealerComments(vehicleDescription.value))
const formattedDealerDescriptionShort = computed(() => {
  const text = formattedDealerDescription.value
  return text.length > 300 ? text.substring(0, 300) : text
})

// Market position analysis computed properties
const marketPositionIcon = computed(() => {
  if (!enrichmentData.value?.marketData) return 'lucide:trending-down'
  
  switch (enrichmentData.value.marketData.marketPosition) {
    case 'below':
      return 'lucide:trending-down'
    case 'above':
      return 'lucide:trending-up'
    default:
      return 'lucide:minus'
  }
})

const marketPositionText = computed(() => {
  if (!enrichmentData.value?.marketData || !vehiclePrice.value) return 'Competitive market pricing'
  
  const marketData = enrichmentData.value.marketData
  const currentPrice = vehiclePrice.value
  const averagePrice = marketData.averagePrice
  
  if (averagePrice <= 0) return 'Competitive market pricing'
  
  const difference = Math.abs(currentPrice - averagePrice)
  const percentageDiff = (difference / averagePrice) * 100
  
  if (marketData.marketPosition === 'below') {
    return `$${difference.toLocaleString()} below market average`
  } else if (marketData.marketPosition === 'above') {
    return `$${difference.toLocaleString()} above market average`
  } else {
    return 'At market average'
  }
})

// Dealer info - this could come from vehicle data or be fetched separately
const dealerInfo = computed(() => {
  // Use consistent phone resolution logic to prevent hydration mismatch
  // Priority: vehicle phone > siteStore phone > reactive phone ref > fallback
  // Important: Avoid using dynamic refs that might not be hydrated consistently
  let consistentPhone = ''
  
  // Only use store values if the store is properly initialized
  if (siteStore && siteStore.isReady) {
    consistentPhone = vehicle.value?.phone || siteStore.phone || phone.value || ''
  } else {
    // During SSR or before store hydration, use vehicle phone or empty string
    consistentPhone = vehicle.value?.phone || ''
  }
  
  // Only apply fallback if we have no phone at all
  if (!consistentPhone) {
    consistentPhone = '03 5222 1811' // Use consistent format without parentheses
  }
  
  const storeReady = !!(siteStore && (siteStore.isReady || siteStore.isInitialized))
  // During SSR/hydration, use static fallbacks to avoid mismatches; after mount use store values
  const safeLogo = (isClientMounted.value && storeReady && siteStore?.logo)
    ? siteStore.logo
    : 'https://images.unsplash.com/photo-1560472355-536de3962603?w=100&h=100&fit=crop'
  const safeName = (isClientMounted.value && storeReady && siteStore?.name)
    ? siteStore.name
    : 'Dealer'

  if (vehicle.value?.address) {
    return {
      name: vehicle.value.sellername || safeName,
      location: `${vehicle.value.address.suburb || ''}, ${vehicle.value.address.state || ''}`.trim(),
      phone: consistentPhone,
      logo: safeLogo,
      rating: 4.8,
      reviewCount: 234,
      closingTime: '6:00 PM',
    }
  }
  return {
    name: safeName || 'Melbourne Dealer',
    location: 'Melbourne, VIC',
    phone: consistentPhone,
    logo: safeLogo,
    rating: 4.8,
    reviewCount: 234,
    closingTime: '6:00 PM',
  }
})

// Fetch vehicle data
async function fetchVehicleData() {
  if (!vehicleId) {
    error.value = 'No vehicle ID provided'
    isLoading.value = false
    return
  }

  try {
    isLoading.value = true
    error.value = null
    
          // Ensure stores are initialized (for client-side navigation)
    if (!vehiclesStore) {
      vehiclesStore = useVehiclesStore()
      vehicleStore = useVehicleStore()
      ancapStore = useANCAPStore()
      siteStore = useSiteStore()
      
      // Update reactive refs from site store
      syncStoreRefs()
    }
    
    // Fetch vehicle from store
    const fetchedVehicle = await vehiclesStore.fetchVehicleById(vehicleId)
    
    if (fetchedVehicle) {
      vehicle.value = fetchedVehicle
      
      // Track view and add to recently viewed (guarded to avoid double increments)
      await trackViewOnce()
      
      // Set initial selected image
      await nextTick()
      if (vehicleImages.value.length > 0) {
        selectedImage.value = vehicleImages.value[0] || ''
      }

      // Fetch ANCAP data if we have the required vehicle information
      if (vehicleMake.value && vehicleModel.value && vehicleYear.value) {
        try {
          await ancapStore.ensureRating(
            vehicleMake.value,
            vehicleModel.value,
            parseInt(String(vehicleYear.value))
          )
        } catch (ancapError) {
          console.log('[Vehicle Detail] ANCAP data not available:', ancapError)
          // Don't fail the page load if ANCAP data is unavailable
        }
      }

      // Fetch market intelligence data
      await nextTick() // Ensure vehicle data is reactive before fetching market intelligence
      console.log('🔍 [Market Intelligence] Vehicle data check:', {
        make: vehicleMake.value,
        model: vehicleModel.value,
        year: vehicleYear.value,
        price: vehiclePrice.value
      })
      if (vehicleMake.value && vehicleModel.value && vehicleYear.value && vehiclePrice.value) {
        console.log('🚀 [Market Intelligence] Scheduling market intelligence fetch')
        scheduleFetchMarketIntelligence()
      } else {
        console.log('❌ [Market Intelligence] Not called - missing required data')
      }
    } else {
      error.value = 'Vehicle not found'
    }
  } catch (err) {
    console.error('Error fetching vehicle:', err)
    error.value = 'Failed to load vehicle data'
  } finally {
    isLoading.value = false
  }
}

// Fetch market intelligence data
async function fetchMarketIntelligence() {
  if (!vehicle.value || !vehicleMake.value || !vehicleModel.value || !vehicleYear.value || !vehiclePrice.value) {
    console.log('[MarketIntelligence] Missing required vehicle data for market analysis')
    return
  }

  try {
    isLoadingMarketIntelligence.value = true
    marketIntelligenceError.value = null

    const response = await $fetch('/api/vehicle-intelligence/market-analysis', {
      method: 'POST',
      body: {
        vehicleId: vehicle.value.id,
        make: vehicleMake.value,
        model: vehicleModel.value,
        year: vehicleYear.value,
        price: vehiclePrice.value,
        kms: vehicleKms.value,
        state: vehicle.value?.address?.state || 'QLD'
      }
    })

    marketIntelligence.value = response
    console.log('[MarketIntelligence] Successfully fetched market data:', response)
  } catch (error) {
    console.error('[MarketIntelligence] Error fetching market data:', error)
    marketIntelligenceError.value = 'Unable to load market intelligence'
  } finally {
    isLoadingMarketIntelligence.value = false
  }
}



// Determine the correct department based on vehicle condition
const currentDepartment = computed(() => {
  if (!departments.value || !isReady.value) {
    // Return a fallback department object to prevent empty data
    // Use consistent phone format without parentheses to avoid hydration issues
    return {
      id: '1',
      phone: siteStore?.phone || '03 5222 1811',
      address: siteStore?.showroom_address || 'Address not available',
      map_embed: '',
      map_directions: '',
      trading: {
        notice: []
      }
    }
  }
  
  // Check if vehicle is new or demo (use sales department)
  const condition = vehicleCondition.value?.toLowerCase()
  const isUsed = condition === 'used' || condition === 'pre-owned'
  
  let selectedDept = null
  const depts = departments.value as any
  if (isUsed && depts.used_sales && depts.used_sales.address) {
    selectedDept = depts.used_sales
  } else if (depts.sales && depts.sales.address) {
    selectedDept = depts.sales
  }
  
  // If no department has data, return fallback
  if (!selectedDept || !selectedDept.address) {
    // Use consistent phone format without parentheses to avoid hydration issues
    return {
      id: '1',
      phone: siteStore?.phone || '03 5222 1811',
      address: siteStore?.showroom_address || 'Address not available',
      map_embed: '',
      map_directions: '',
      trading: {
        notice: []
      }
    }
  }
  
  return selectedDept
})



// Dynamic feature categories from enrichment data with fallbacks
const featureCategories = computed(() => {
  if (enrichmentData.value && enrichmentData.value.features) {
    return [
      {
        name: enrichmentData.value.features.safety.name,
        icon: 'lucide:shield',
        features: enrichmentData.value.features.safety.features.length > 0 
          ? enrichmentData.value.features.safety.features 
          : ['Forward Collision Avoidance', 'Blind Spot Monitoring', 'Lane Keep Assist', 'Rear Cross Traffic Alert'],
      },
      {
        name: enrichmentData.value.features.comfort.name,
        icon: 'lucide:thermometer',
        features: enrichmentData.value.features.comfort.features.length > 0 
          ? enrichmentData.value.features.comfort.features 
          : ['Heated Front Seats', 'Dual Zone Climate Control', 'Wireless Phone Charging', 'Smart Key Entry'],
      },
      {
        name: enrichmentData.value.features.technology.name,
        icon: 'lucide:smartphone',
        features: enrichmentData.value.features.technology.features.length > 0 
          ? enrichmentData.value.features.technology.features 
          : ['12.3" Touchscreen', 'Apple CarPlay/Android Auto', 'Premium Sound System', 'Navigation System'],
      },
      {
        name: enrichmentData.value.features.performance.name,
        icon: 'lucide:zap',
        features: enrichmentData.value.features.performance.features.length > 0 
          ? enrichmentData.value.features.performance.features 
          : ['All-Wheel Drive', 'Sport Mode', 'Paddle Shifters', 'Traction Control'],
      },
    ]
  }
  
  // Fallback to static data when enrichment is not available
  return [
    {
      name: 'Safety & Security',
      icon: 'lucide:shield',
      features: ['Forward Collision Avoidance', 'Blind Spot Monitoring', 'Lane Keep Assist', 'Rear Cross Traffic Alert'],
    },
    {
      name: 'Comfort & Convenience',
      icon: 'lucide:thermometer',
      features: ['Heated Front Seats', 'Dual Zone Climate Control', 'Wireless Phone Charging', 'Smart Key Entry'],
    },
    {
      name: 'Technology',
      icon: 'lucide:smartphone',
      features: ['12.3" Touchscreen', 'Apple CarPlay/Android Auto', 'Premium Sound System', 'Navigation System'],
    },
    {
      name: 'Performance',
      icon: 'lucide:zap',
      features: ['All-Wheel Drive', 'Sport Mode', 'Paddle Shifters', 'Traction Control'],
    },
  ]
})

// Dynamic specification groups from enrichment data with fallbacks
const specificationGroups = computed(() => {
  const groups = []
  
  if (enrichmentData.value && enrichmentData.value.specifications) {
    const specs = enrichmentData.value.specifications
    
    // Electric Vehicle Specifications (prioritize over engine)
    if (specs.electric) {
      const electricSpecs = []
      if (specs.electric.batteryCapacity) electricSpecs.push({ label: 'Battery Capacity', value: specs.electric.batteryCapacity })
      if (specs.electric.range) electricSpecs.push({ label: 'WLTP Range', value: specs.electric.range })
      if (specs.electric.motorPower) electricSpecs.push({ label: 'Motor Power', value: specs.electric.motorPower })
      if (specs.electric.motorTorque) electricSpecs.push({ label: 'Motor Torque', value: specs.electric.motorTorque })
      if (specs.electric.drivetrain) electricSpecs.push({ label: 'Drivetrain', value: specs.electric.drivetrain })
      if (specs.efficiency.energyConsumption) electricSpecs.push({ label: 'Energy Consumption', value: specs.efficiency.energyConsumption })
      
      if (electricSpecs.length > 0) {
        groups.push({
          name: 'Electric Motor & Battery',
          specs: electricSpecs
        })
      }
      
      // Charging Specifications for EVs
      const chargingSpecs = []
      if (specs.electric.chargingAC) chargingSpecs.push({ label: 'AC Charging', value: `${specs.electric.chargingAC} kW` })
      if (specs.electric.chargingDC) chargingSpecs.push({ label: 'DC Fast Charging', value: `${specs.electric.chargingDC} kW` })
      if (specs.electric.chargingTime?.home) chargingSpecs.push({ label: 'Home Charging (0-100%)', value: specs.electric.chargingTime.home })
      if (specs.electric.chargingTime?.dc) chargingSpecs.push({ label: 'DC Fast Charging (10-80%)', value: specs.electric.chargingTime.dc })
      if (specs.efficiency.chargingCost) chargingSpecs.push({ label: 'Charging Cost (per 100km)', value: specs.efficiency.chargingCost })
      
      if (chargingSpecs.length > 0) {
        groups.push({
          name: 'Charging & Efficiency',
          specs: chargingSpecs
        })
      }
    }
    // ICE Engine Specifications (only if not electric)
    else if (specs.engine) {
      const engineSpecs = []
      if (specs.engine.displacement) engineSpecs.push({ label: 'Engine', value: specs.engine.displacement })
      if (specs.engine.power) engineSpecs.push({ label: 'Power', value: specs.engine.power })
      if (specs.engine.torque) engineSpecs.push({ label: 'Torque', value: specs.engine.torque })
      if (specs.engine.cylinders) engineSpecs.push({ label: 'Cylinders', value: specs.engine.cylinders.toString() })
      if (specs.engine.configuration) engineSpecs.push({ label: 'Configuration', value: specs.engine.configuration })
      if (specs.efficiency.fuelEconomyCombined) engineSpecs.push({ label: 'Fuel Economy', value: specs.efficiency.fuelEconomyCombined })
      
      // Add vehicle data if enrichment doesn't have it
      if (vehicleTransmission.value && !engineSpecs.find(s => s.label === 'Transmission')) {
        engineSpecs.push({ label: 'Transmission', value: vehicleTransmission.value })
      }
      if (vehicleDrivetrain.value && !engineSpecs.find(s => s.label === 'Drivetrain')) {
        engineSpecs.push({ label: 'Drivetrain', value: vehicleDrivetrain.value })
      }
      
      if (engineSpecs.length > 0) {
        groups.push({
          name: 'Engine & Performance',
          specs: engineSpecs
        })
      }
    }
    
    // Dimensions & Capacity Group
    const dimensionSpecs = []
    if (specs.dimensions.length) dimensionSpecs.push({ label: 'Length', value: specs.dimensions.length })
    if (specs.dimensions.width) dimensionSpecs.push({ label: 'Width', value: specs.dimensions.width })
    if (specs.dimensions.height) dimensionSpecs.push({ label: 'Height', value: specs.dimensions.height })
    if (specs.dimensions.wheelbase) dimensionSpecs.push({ label: 'Wheelbase', value: specs.dimensions.wheelbase })
    if (specs.dimensions.groundClearance || specs.dimensions.ground_clearance) {
      dimensionSpecs.push({ label: 'Ground Clearance', value: specs.dimensions.groundClearance || specs.dimensions.ground_clearance })
    }
    if (specs.dimensions.kerbWeight) dimensionSpecs.push({ label: 'Kerb Weight', value: specs.dimensions.kerbWeight })
    if (specs.efficiency.fuelTankCapacity) dimensionSpecs.push({ label: 'Fuel Tank', value: specs.efficiency.fuelTankCapacity })
    
    // Add vehicle data if enrichment doesn't have it
    if (vehicleSeats.value && !dimensionSpecs.find(s => s.label === 'Seating')) {
      dimensionSpecs.push({ label: 'Seating', value: `${vehicleSeats.value} passengers` })
    }
    if (vehicleDoors.value && !dimensionSpecs.find(s => s.label === 'Doors')) {
      dimensionSpecs.push({ label: 'Doors', value: vehicleDoors.value })
    }
    
    if (dimensionSpecs.length > 0) {
      groups.push({
        name: 'Dimensions & Capacity',
        specs: dimensionSpecs
      })
    }
    
    // Performance Group (if we have performance data)
    const performanceSpecs = []
    if (specs.performance.acceleration) performanceSpecs.push({ label: '0-100km/h', value: specs.performance.acceleration })
    if (specs.performance.topSpeed) performanceSpecs.push({ label: 'Top Speed', value: specs.performance.topSpeed })
    if (specs.performance.turningCircle) performanceSpecs.push({ label: 'Turning Circle', value: specs.performance.turningCircle })
    if (specs.efficiency.co2Emissions) performanceSpecs.push({ label: 'CO2 Emissions', value: specs.efficiency.co2Emissions })
    
    if (performanceSpecs.length > 0) {
      groups.push({
        name: 'Performance & Efficiency',
        specs: performanceSpecs
      })
    }

    // Fuel Economy & Efficiency (additional section for detailed consumption data)
    if (specs.efficiency) {
      const efficiencySpecs = []
      if (specs.efficiency.fuelEconomyCombined) efficiencySpecs.push({ label: 'Combined Fuel Economy', value: specs.efficiency.fuelEconomyCombined })
      if (specs.efficiency.fuelEconomyUrban) efficiencySpecs.push({ label: 'Urban Fuel Economy', value: specs.efficiency.fuelEconomyUrban })
      if (specs.efficiency.fuelEconomyExtraUrban) efficiencySpecs.push({ label: 'Extra Urban Fuel Economy', value: specs.efficiency.fuelEconomyExtraUrban })
      if (specs.efficiency.fuelTankCapacity) efficiencySpecs.push({ label: 'Fuel Tank Capacity', value: specs.efficiency.fuelTankCapacity })
      if (specs.efficiency.co2Emissions && !performanceSpecs.find(s => s.label === 'CO2 Emissions')) {
        efficiencySpecs.push({ label: 'CO2 Emissions', value: specs.efficiency.co2Emissions })
      }

      if (efficiencySpecs.length > 0) {
        groups.push({
          name: 'Fuel Economy & Emissions',
          specs: efficiencySpecs
        })
      }
    }

    // Advanced Engine Specifications
    if (specs.engine) {
      const advancedEngineSpecs = []
      if (specs.engine.fuel_system || specs.engine.fuelSystem) {
        advancedEngineSpecs.push({ label: 'Fuel System', value: specs.engine.fuel_system || specs.engine.fuelSystem })
      }
      if (specs.engine.fuel_type) advancedEngineSpecs.push({ label: 'Fuel Type', value: specs.engine.fuel_type })
      if (specs.engine.valves) advancedEngineSpecs.push({ label: 'Valves', value: specs.engine.valves.toString() })
      if (specs.engine.compression_ratio) advancedEngineSpecs.push({ label: 'Compression Ratio', value: specs.engine.compression_ratio })
      
      if (advancedEngineSpecs.length > 0) {
        groups.push({
          name: 'Advanced Engine Details',
          specs: advancedEngineSpecs
        })
      }
    }

    // Drivetrain Specifications
    if (specs.drivetrain) {
      const drivetrainSpecs = []
      if (specs.drivetrain.drive_type) drivetrainSpecs.push({ label: 'Drive Type', value: specs.drivetrain.drive_type })
      if (specs.drivetrain.transmission) drivetrainSpecs.push({ label: 'Transmission', value: specs.drivetrain.transmission })
      if (specs.drivetrain.differentials) drivetrainSpecs.push({ label: 'Differentials', value: specs.drivetrain.differentials })
      if (specs.drivetrain.final_drive_ratio) drivetrainSpecs.push({ label: 'Final Drive Ratio', value: specs.drivetrain.final_drive_ratio })

      if (drivetrainSpecs.length > 0) {
        groups.push({
          name: 'Drivetrain & Transmission',
          specs: drivetrainSpecs
        })
      }
    }

    // Extended Dimensions
    if (specs.dimensions) {
      const extendedDimensionSpecs = []
      if (specs.dimensions.front_track) extendedDimensionSpecs.push({ label: 'Front Track', value: specs.dimensions.front_track })
      if (specs.dimensions.rear_track) extendedDimensionSpecs.push({ label: 'Rear Track', value: specs.dimensions.rear_track })
      if (specs.dimensions.approach_angle) extendedDimensionSpecs.push({ label: 'Approach Angle', value: specs.dimensions.approach_angle })
      if (specs.dimensions.departure_angle) extendedDimensionSpecs.push({ label: 'Departure Angle', value: specs.dimensions.departure_angle })
      if (specs.dimensions.wading_depth) extendedDimensionSpecs.push({ label: 'Wading Depth', value: specs.dimensions.wading_depth })

      if (extendedDimensionSpecs.length > 0) {
        groups.push({
          name: 'Off-Road Capabilities',
          specs: extendedDimensionSpecs
        })
      }
    }

    // Fuel Consumption & Towing
    if (specs.performance && (specs.performance.fuel_consumption || specs.performance.towing_capacity)) {
      const towingFuelSpecs = []
      if (specs.performance.fuel_consumption?.urban) towingFuelSpecs.push({ label: 'Urban Fuel Consumption', value: specs.performance.fuel_consumption.urban })
      if (specs.performance.fuel_consumption?.extra_urban) towingFuelSpecs.push({ label: 'Extra Urban Consumption', value: specs.performance.fuel_consumption.extra_urban })
      if (specs.performance.fuel_consumption?.combined) towingFuelSpecs.push({ label: 'Combined Consumption', value: specs.performance.fuel_consumption.combined })
      if (specs.performance.towing_capacity) towingFuelSpecs.push({ label: 'Towing Capacity', value: specs.performance.towing_capacity })
      if (specs.performance.braked_towing_capacity) towingFuelSpecs.push({ label: 'Braked Towing Capacity', value: specs.performance.braked_towing_capacity })
      if (specs.performance.payload_capacity) towingFuelSpecs.push({ label: 'Payload Capacity', value: specs.performance.payload_capacity })
      if (specs.performance.gross_vehicle_mass) towingFuelSpecs.push({ label: 'Gross Vehicle Mass', value: specs.performance.gross_vehicle_mass })

      if (towingFuelSpecs.length > 0) {
        groups.push({
          name: 'Towing & Load Capacity',
          specs: towingFuelSpecs
        })
      }
    }

    // Suspension & Steering
    if (specs.suspension) {
      const suspensionSpecs = []
      if (specs.suspension.front) suspensionSpecs.push({ label: 'Front Suspension', value: specs.suspension.front })
      if (specs.suspension.rear) suspensionSpecs.push({ label: 'Rear Suspension', value: specs.suspension.rear })

      if (suspensionSpecs.length > 0) {
        groups.push({
          name: 'Suspension & Steering',
          specs: suspensionSpecs
        })
      }
    }

    // Brakes & Safety Systems
    if (specs.brakes) {
      const brakeSpecs = []
      if (specs.brakes.front) brakeSpecs.push({ label: 'Front Brakes', value: specs.brakes.front })
      if (specs.brakes.rear) brakeSpecs.push({ label: 'Rear Brakes', value: specs.brakes.rear })
      if (specs.brakes.safety_systems) brakeSpecs.push({ label: 'Brake Safety Systems', value: specs.brakes.safety_systems })

      if (brakeSpecs.length > 0) {
        groups.push({
          name: 'Braking System',
          specs: brakeSpecs
        })
      }
    }

    // Wheels & Tyres
    if (specs.wheels) {
      const wheelSpecs = []
      if (specs.wheels.size) wheelSpecs.push({ label: 'Wheel Size', value: specs.wheels.size })
      if (specs.wheels.tyres) wheelSpecs.push({ label: 'Tyre Size', value: specs.wheels.tyres })
      if (specs.wheels.spare_wheel) wheelSpecs.push({ label: 'Spare Wheel', value: specs.wheels.spare_wheel })

      if (wheelSpecs.length > 0) {
        groups.push({
          name: 'Wheels & Tyres',
          specs: wheelSpecs
        })
      }
    }

    // Safety Features
    if (specs.safety) {
      const safetySpecs = []
      if (specs.safety.airbags) safetySpecs.push({ label: 'Airbags', value: specs.safety.airbags })
      if (specs.safety.ancap_rating) safetySpecs.push({ label: 'ANCAP Rating', value: specs.safety.ancap_rating })
      if (specs.safety.stability_systems) safetySpecs.push({ label: 'Stability Systems', value: specs.safety.stability_systems })
      if (specs.safety.anti_lock_braking_system) safetySpecs.push({ label: 'Anti-lock Braking', value: specs.safety.anti_lock_braking_system })

      if (safetySpecs.length > 0) {
        groups.push({
          name: 'Safety Features',
          specs: safetySpecs
        })
      }
    }

    // Interior Features
    if (specs.interior) {
      const interiorSpecs = []
      if (specs.interior.seating) interiorSpecs.push({ label: 'Seating Configuration', value: specs.interior.seating })
      if (specs.interior.climate) interiorSpecs.push({ label: 'Climate Control', value: specs.interior.climate })
      if (specs.interior.infotainment) interiorSpecs.push({ label: 'Infotainment System', value: specs.interior.infotainment })
      if (specs.interior.audio) interiorSpecs.push({ label: 'Audio System', value: specs.interior.audio })

      if (interiorSpecs.length > 0) {
        groups.push({
          name: 'Interior & Comfort',
          specs: interiorSpecs
        })
      }
    }

    // Exterior Features
    if (specs.exterior) {
      const exteriorSpecs = []
      if (specs.exterior.lighting) exteriorSpecs.push({ label: 'Lighting', value: specs.exterior.lighting })
      if (specs.exterior.accessories) exteriorSpecs.push({ label: 'Exterior Accessories', value: specs.exterior.accessories })

      if (exteriorSpecs.length > 0) {
        groups.push({
          name: 'Exterior Features',
          specs: exteriorSpecs
        })
      }
    }

    // Warranty Information
    if (specs.warranty) {
      const warrantySpecs = []
      if (specs.warranty.vehicle) warrantySpecs.push({ label: 'Vehicle Warranty', value: specs.warranty.vehicle })
      if (specs.warranty.powertrain) warrantySpecs.push({ label: 'Powertrain Warranty', value: specs.warranty.powertrain })
      if (specs.warranty.corrosion) warrantySpecs.push({ label: 'Corrosion Warranty', value: specs.warranty.corrosion })
      if (specs.warranty.roadside_assistance) warrantySpecs.push({ label: 'Roadside Assistance', value: specs.warranty.roadside_assistance })

      if (warrantySpecs.length > 0) {
        groups.push({
          name: 'Warranty & Support',
          specs: warrantySpecs
        })
      }
    }
  }
  
  // Only show basic vehicle data if no enrichment available - no misleading fallback specs
  if (groups.length === 0) {
    const basicSpecs = []
    
    // Only add specs we actually have from vehicle data
    if (vehicleEngine.value) basicSpecs.push({ label: 'Engine', value: vehicleEngine.value })
    if (vehicleTransmission.value) basicSpecs.push({ label: 'Transmission', value: vehicleTransmission.value })
    if (vehicleDrivetrain.value) basicSpecs.push({ label: 'Drivetrain', value: vehicleDrivetrain.value })
    if (vehicleFuel.value) basicSpecs.push({ label: 'Fuel Type', value: vehicleFuel.value })
    if (vehicleBody.value) basicSpecs.push({ label: 'Body Style', value: vehicleBody.value })
    if (vehicleSeats.value) basicSpecs.push({ label: 'Seating', value: `${vehicleSeats.value} passengers` })
    if (vehicleDoors.value) basicSpecs.push({ label: 'Doors', value: vehicleDoors.value })
    
    if (basicSpecs.length > 0) {
      return [
        {
          name: 'Basic Vehicle Information',
          specs: basicSpecs
        }
      ]
    }
    
    // If no vehicle data available, return empty array
    return []
  }
  
  return groups
})

// Dynamic service history from vehicle data
const serviceHistory = computed(() => {
  return vehicle.value?.serviceHistory || []
})

// Dynamic ownership history from vehicle data
const ownershipHistory = computed(() => {
  return vehicle.value?.ownershipHistory || []
})

// Tab configuration
const tabsConfig = [
  { id: 'features', name: 'Features' },
  { id: 'overview', name: 'Overview' },
  { id: 'specifications', name: 'Specifications' },
  { id: 'history', name: 'Market Analysis' }
]

// Customer reviews removed - not needed for this implementation

// Related vehicles computed from store data
const relatedVehicles = computed(() => {
  // Ensure consistent behavior between server and client
  if (!vehicle.value || !vehiclesStore?.vehicles || vehiclesStore.vehicles.length === 0) {
    return []
  }
  
  // Do not short-circuit during hydration; allow SSR-rendered list to hydrate correctly

  const currentVehicle = vehicle.value
  const allVehicles = vehiclesStore.vehicles.filter((v: any) => v.id !== currentVehicle.id)
  
  // Helper function to get vehicle image
  const getVehicleImage = (v: any) => {
    if (v.thumb) return v.thumb
    if (v.photos && v.photos.length > 0) return v.photos[0]
    if (v.image) return v.image
    return 'https://placehold.co/600x400?text=No+Image'
  }

  // Helper function to get vehicle title
  const getVehicleTitle = (v: any) => {
    if (v.title) return v.title
    const make = v.make?.displayValue?.[0] || v.make?.value?.[0] || ''
    const model = v.model?.displayValue?.[0] || v.model?.value?.[0] || ''
    const year = typeof v.year === 'number' ? v.year.toString() : (v.year?.value?.[0] || '')
    return `${year} ${make} ${model}`.trim()
  }

  // Scoring function for similarity
  const calculateSimilarityScore = (v: any) => {
    let score = 0
    
    // Same make (highest priority)
    const currentMake = currentVehicle.make?.value?.[0]?.toLowerCase()
    const vehicleMake = v.make?.value?.[0]?.toLowerCase()
    if (currentMake && vehicleMake && currentMake === vehicleMake) {
      score += 50
    }

    // Same model (very high priority)
    const currentModel = currentVehicle.model?.value?.[0]?.toLowerCase()
    const vehicleModel = v.model?.value?.[0]?.toLowerCase()
    if (currentModel && vehicleModel && currentModel === vehicleModel) {
      score += 40
    }

    // Same body type (high priority)
    const currentBody = currentVehicle.body?.value?.[0]?.toLowerCase()
    const vehicleBody = v.body?.value?.[0]?.toLowerCase()
    if (currentBody && vehicleBody && currentBody === vehicleBody) {
      score += 30
    }

    // Similar price range (within 20%)
    const currentPrice = Number(currentVehicle.price || 0)
    const vehiclePrice = Number(v.price || 0)
    if (currentPrice > 0 && vehiclePrice > 0) {
      const priceDiff = Math.abs(currentPrice - vehiclePrice) / currentPrice
      if (priceDiff <= 0.2) {
        score += 20
      } else if (priceDiff <= 0.4) {
        score += 10
      }
    }

    // Same transmission
    const currentTransmission = currentVehicle.transmission?.value?.[0]?.toLowerCase()
    const vehicleTransmission = v.transmission?.value?.[0]?.toLowerCase()
    if (currentTransmission && vehicleTransmission && currentTransmission === vehicleTransmission) {
      score += 15
    }

    // Same fuel type
    const currentFuel = currentVehicle.fuel?.value?.[0]?.toLowerCase()
    const vehicleFuel = v.fuel?.value?.[0]?.toLowerCase()
    if (currentFuel && vehicleFuel && currentFuel === vehicleFuel) {
      score += 15
    }

    // Similar year (within 3 years)
    const currentYear = typeof currentVehicle.year === 'number' ? currentVehicle.year : Number(currentVehicle.year?.value?.[0] || 0)
    const vehicleYear = typeof v.year === 'number' ? v.year : Number(v.year?.value?.[0] || 0)
    if (currentYear > 0 && vehicleYear > 0) {
      const yearDiff = Math.abs(currentYear - vehicleYear)
      if (yearDiff <= 1) {
        score += 15
      } else if (yearDiff <= 3) {
        score += 10
      } else if (yearDiff <= 5) {
        score += 5
      }
    }

    // Same condition
    const currentCondition = currentVehicle.condition?.value?.[0]?.toLowerCase()
    const vehicleCondition = v.condition?.value?.[0]?.toLowerCase()
    if (currentCondition && vehicleCondition && currentCondition === vehicleCondition) {
      score += 10
    }

    return score
  }

  // Score and sort vehicles by similarity
  const scoredVehicles = allVehicles
    .map((v: any) => ({
      vehicle: v,
      score: calculateSimilarityScore(v)
    }))
    .filter((item: any) => item.score > 0) // Only include vehicles with some similarity
    .sort((a: any, b: any) => b.score - a.score) // Sort by highest score first
    .slice(0, 6) // Take top 6 similar vehicles

  // Transform to the format expected by the template
  return scoredVehicles.map((item: any) => ({
    id: item.vehicle.id,
    stockid: item.vehicle.stockid,
    name: getVehicleTitle(item.vehicle),
    price: Number(item.vehicle.price || 0),
    mileage: Number(item.vehicle.kms || item.vehicle.mileage || 0),
    image: getVehicleImage(item.vehicle),
    slug: item.vehicle.slug || 'details',
    make: item.vehicle.make?.displayValue?.[0] || item.vehicle.make?.value?.[0] || '',
    model: item.vehicle.model?.displayValue?.[0] || item.vehicle.model?.value?.[0] || '',
    body: item.vehicle.body?.displayValue?.[0] || item.vehicle.body?.value?.[0] || 'SUV',
    transmission: item.vehicle.transmission?.displayValue?.[0] || item.vehicle.transmission?.value?.[0] || 'Automatic',
    fuel: item.vehicle.fuel?.displayValue?.[0] || item.vehicle.fuel?.value?.[0] || 'Petrol',
    condition: item.vehicle.condition?.displayValue?.[0] || item.vehicle.condition?.value?.[0] || 'Used',
    year: typeof item.vehicle.year === 'number' ? item.vehicle.year.toString() : (item.vehicle.year?.value?.[0] || ''),
  }))
})

// Dialog state
const showTestDriveDialog = ref(false)
const showContactDialog = ref(false)
const showFinanceDialog = ref(false)
const showInquiryDialog = ref(false)
const showAIChatDialog = ref(false)

// Methods (image navigation now handled by VehicleGalleryGrid component)

function formatPrice(price: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price)
}

function formatMileage(mileage: number): string {
  return new Intl.NumberFormat('en-AU').format(mileage)
}

function formatDate(date: string | Date): string {
  return new Date(date).toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

function getUsageClassification(): string {
  const vehicleAge = new Date().getFullYear() - (parseInt(vehicleYear.value) || new Date().getFullYear())
  const totalKms = vehicleKms.value
  const kmPerYear = totalKms / Math.max(vehicleAge, 1)
  
  if (kmPerYear < 15000) return 'Low usage'
  if (kmPerYear < 25000) return 'Average usage'
  return 'High usage'
}

function calculateMonthlyPayment(price: number): number {
  // Simple calculation: 5% down, 6% APR, 60 months
  const downPayment = price * 0.05
  const loanAmount = price - downPayment
  const monthlyRate = 0.06 / 12
  const months = 60

  const monthlyPayment = (loanAmount * monthlyRate * (1 + monthlyRate) ** months)
    / ((1 + monthlyRate) ** months - 1)

  return Math.round(monthlyPayment)
}

function handlePrimaryAction() {
  showTestDriveDialog.value = true
}

function handleCall() {
  window.location.href = `tel:${dealerInfo.value.phone}`
}

function handleMessage() {
  showContactDialog.value = true
}

function handleFinanceClick() {
  showFinanceDialog.value = true
}

function handleInquiryClick() {
  showInquiryDialog.value = true
}

function handleDialogSubmit(data: any) {
  console.log('Dialog submitted:', data)
  // Here you would typically send the data to your API
  // For now, we'll just log it
}

// AI Chat event handlers
function handleAITestDrive() {
  showTestDriveDialog.value = true
}

function handleAIContactDealer() {
  showContactDialog.value = true
}

function handleAIFinance() {
  showFinanceDialog.value = true
}

// Helper functions for dealer information
function getFullAddress(): string {
  if (vehicle.value?.address) {
    const addr = vehicle.value.address
    const parts = [
      addr.line1,
      addr.suburb,
      addr.state,
      addr.postcode
    ].filter(Boolean)
    return parts.join(', ')
  }
  
  // Fallback to dealerInfo location
  return dealerInfo.value.location || 'Address not available'
}

function openDirections() {
  const address = getFullAddress()
  const encodedAddress = encodeURIComponent(address)
  
  // Detect platform and open appropriate maps app
  const userAgent = navigator.userAgent.toLowerCase()
  
  if (userAgent.includes('iphone') || userAgent.includes('ipad')) {
    // iOS - prefer Apple Maps
    window.open(`http://maps.apple.com/?q=${encodedAddress}`, '_blank')
  } else if (userAgent.includes('android')) {
    // Android - prefer Google Maps app
    window.open(`geo:0,0?q=${encodedAddress}`, '_blank')
  } else {
    // Desktop/Web - use Google Maps
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank')
  }
}

// Format dealer comments for better readability
function formatDealerComments(text: string): string {
  if (!text) return ''
  
  return text
    // Decode HTML entities
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    // Fix malformed bullet points like "one• stop" to "one-stop"
    .replace(/(\w)•\s*(\w)/g, '$1-$2')
    // Convert multiple spaces to single space
    .replace(/\s+/g, ' ')
    // Add line breaks after sentences that end with periods, exclamation marks, or question marks
    .replace(/([.!?])\s+(?=[A-Z])/g, '$1\n\n')
    // Add line breaks after common separators like commas followed by capital letters (for lists)
    .replace(/,\s+(?=[A-Z][A-Z\s]+[,.])/g, ',\n• ')
    // Clean up and format proper bullet points at start of lines
    .replace(/[\*\-\•]\s*/g, '• ')
    // Ensure proper spacing around bullet points
    .replace(/\n•/g, '\n• ')
    // Remove excessive line breaks
    .replace(/\n{3,}/g, '\n\n')
    // Trim whitespace
    .trim()
}

async function submitInquiry() {
  submittingInquiry.value = true

  try {
    // Submit inquiry to API
    await new Promise(resolve => setTimeout(resolve, 1000)) // Simulate API call

    // Reset form
    inquiry.value = {
      name: '',
      phone: '',
      email: '',
      message: '',
    }

    // Show success message
    alert('Inquiry sent successfully! We\'ll contact you within 15 minutes.')
  }
  catch (error) {
    alert('Failed to send inquiry. Please try again.')
  }
  finally {
    submittingInquiry.value = false
  }
}

// Legacy client-side enrichment function - Now handled by SSR
// Keeping for reference but no longer used
async function fetchEnrichmentData() {
  // This function is deprecated - enrichment data is now fetched server-side via useAsyncData
  console.warn('[VehicleDetail] fetchEnrichmentData() is deprecated - enrichment now handled by SSR')
}

// Watch for route changes to handle back/forward navigation
watch(() => route.params.id, async (newId, oldId) => {
  if (newId && newId !== oldId) {
    await fetchVehicleData()
    // Note: enrichment data is handled by SSR in the main component setup
    // No need for client-side fetchEnrichmentData() on route changes
  }
}, { immediate: false })

// Watch for store hydration on client side
if (process.client) {
  watch(() => siteStore?.isInitialized, (isInitialized) => {
    if (isInitialized) {
      // Sync reactive refs when store is fully hydrated
      syncStoreRefs()
    }
  }, { immediate: true })
}

// SSR Data Fetching - Fetch vehicle, ANCAP, and enrichment data during server-side rendering
const { data: vehicleData, error: vehicleFetchError } = await useAsyncData(
  `vehicle-${vehicleId}`,
  async () => {
    try {
      // Initialize stores within useAsyncData where Pinia is guaranteed to be available
      vehiclesStore = useVehiclesStore()
      vehicleStore = useVehicleStore()
      ancapStore = useANCAPStore()
      siteStore = useSiteStore()
      
      // Update reactive refs from site store
      syncStoreRefs()
      
      // Fetch vehicle from store
      const fetchedVehicle = await vehiclesStore.fetchVehicleById(vehicleId)
      
      if (fetchedVehicle) {
        // Extract vehicle details for ANCAP lookup
        const make = fetchedVehicle.make?.displayValue?.[0] || ''
        const model = fetchedVehicle.model?.displayValue?.[0] || ''
        const yearValue = typeof fetchedVehicle.year === 'number' 
          ? fetchedVehicle.year 
          : fetchedVehicle.year?.value?.[0] || 0
        const year = parseInt(String(yearValue))
        
        // Fetch ANCAP data if we have the required vehicle information
        if (make && model && year) {
          try {
            await ancapStore.ensureRating(make, model, year)
          } catch (ancapError) {
            console.log('[Vehicle SSR] ANCAP data not available:', ancapError)
            // Don't fail the page load if ANCAP data is unavailable
          }
        }
        
        return fetchedVehicle
      } else {
        throw createError({
          statusCode: 404,
          statusMessage: 'Vehicle not found'
        })
      }
    } catch (err) {
      console.error('Error fetching vehicle during SSR:', err)
      throw createError({
        statusCode: 500,
        statusMessage: 'Failed to load vehicle data'
      })
    }
  }
)

// SSR Enrichment Data Fetching - Fetch enrichment data server-side
const { data: enrichmentSSRData, error: enrichmentFetchError } = await useAsyncData(
  `enrichment-${vehicleId}`,
  async () => {
    // Only fetch if vehicle data was successfully loaded
    if (!vehicleData.value) return null
    
    try {
      const enrichmentData = await $fetch(`/api/vehicle-enrichment/${vehicleId}`) as VehicleEnrichment
      return enrichmentData
    } catch (enrichmentError) {
      console.warn('[Vehicle SSR] Failed to load enrichment data server-side:', enrichmentError)
      // Don't fail the page load if enrichment data is unavailable
      return null
    }
  },
  {
    // Make enrichment dependent on vehicle data
    default: () => null
  }
)

// Set vehicle data from SSR
if (vehicleData.value) {
  vehicle.value = vehicleData.value
}

// Set enrichment data from SSR
if (enrichmentSSRData.value) {
  enrichmentData.value = enrichmentSSRData.value
  isLoadingEnrichment.value = false
} else if (enrichmentFetchError.value) {
  enrichmentError.value = 'Failed to load enhanced vehicle data'
  isLoadingEnrichment.value = false
}

// Initialize site store on server if needed
if (process.server && siteStore) {
  await siteStore.serverInit()
}

// Handle fetch errors
if (vehicleFetchError.value) {
  error.value = vehicleFetchError.value.message || 'Failed to load vehicle'
}

// Client-side initialization for additional data and tracking
onMounted(async () => {
  // Ensure page starts at the top when component mounts
  if (process.client) {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }
  
  // Initialize site store first to ensure departments data is available
  try {
    if (siteStore) {
      await siteStore.initialize()
      // Sync reactive refs after store initialization
      syncStoreRefs()
    }
  } catch (error) {
    console.error('[VehicleDetail] Failed to initialize site store:', error)
  }
  
  if (vehicle.value && vehicleStore) {
    // Track view and add to recently viewed (guarded to avoid double increments)
    await trackViewOnce()
    
    // Set initial selected image
    await nextTick()
    if (vehicleImages.value.length > 0) {
      selectedImage.value = vehicleImages.value[0] || ''
    }
    
    // Enrichment data is now loaded server-side via SSR - no client-side fetch needed
    // Schedule market intelligence fetch during idle time to avoid blocking initial render
    if (vehicleMake.value && vehicleModel.value && vehicleYear.value && vehiclePrice.value) {
      scheduleFetchMarketIntelligence()
    }
    
  } else if (!vehicleFetchError.value) {
    // Only try manual fetch if SSR didn't handle it and there's no SSR error
    await fetchVehicleData()
    // Note: fetchEnrichmentData() is no longer needed - handled by SSR
  }

  // Lazily observe the map/hours section to defer rendering until near viewport
  if (process.client && mapSectionRef.value) {
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0]
      if (entry && (entry.isIntersecting || entry.intersectionRatio > 0)) {
        isMapSectionVisible.value = true
        observer.disconnect()
      }
    }, { rootMargin: '200px' })
    observer.observe(mapSectionRef.value)
  }
  // Mark client mounted to allow dynamic logo/name without hydration mismatch
  isClientMounted.value = true
})


// Get the best available address for mapping
const getMapAddress = () => {
  
  // Helper function to format a complete address
  const formatCompleteAddress = (street: string, suburb: string, state: string, postcode?: string) => {
    // Expand common abbreviations in street names
    const expandedStreet = street
      .replace(/\bRd\b/gi, 'Road')
      .replace(/\bSt\b/gi, 'Street')
      .replace(/\bAve\b/gi, 'Avenue')
      .replace(/\bDr\b/gi, 'Drive')
      .replace(/\bCr\b/gi, 'Crescent')
      .replace(/\bCt\b/gi, 'Court')
      .replace(/\bPl\b/gi, 'Place')
      .replace(/\bLn\b/gi, 'Lane')
      .replace(/\bBlvd\b/gi, 'Boulevard')
    
    return `${expandedStreet}, ${suburb}, ${state} ${postcode || ''}`.trim()
  }
  
  // Priority order for address:
  // 1. Vehicle's dealer address (if it has full address details)
  const vehicleAddress = vehicle.value?.address
  if (vehicleAddress && vehicleAddress.line1 && vehicleAddress.suburb && vehicleAddress.state) {
    const fullAddress = formatCompleteAddress(
      vehicleAddress.line1, 
      vehicleAddress.suburb, 
      vehicleAddress.state, 
      vehicleAddress.postcode
    )
    return fullAddress
  }
  
  // 2. Enhanced department address (if populated) - try to complete it
  if (currentDepartment.value?.address && currentDepartment.value.address.trim()) {
    const deptAddress = currentDepartment.value.address
    
    // Check if it's a short address like "961 North Rd" and try to enhance it
    if (vehicleAddress && vehicleAddress.suburb && vehicleAddress.state) {
      // If department address looks like just street number/name, combine with vehicle suburb/state
      if (deptAddress.match(/^\d+\s+[\w\s]+\s+(Rd|St|Ave|Dr|Cr|Ct|Pl|Ln|Blvd)$/i)) {
        const enhancedAddress = formatCompleteAddress(
          deptAddress,
          vehicleAddress.suburb,
          vehicleAddress.state,
          vehicleAddress.postcode
        )
        return enhancedAddress
      }
    }
    
    // Use department address as-is if it seems complete
    return deptAddress
  }
  
  // 3. Site store showroom address (if available)
  if (siteStore.showroom_address && siteStore.showroom_address.trim()) {
    return siteStore.showroom_address
  }
  
  // 4. Partial vehicle address (suburb, state)
  if (vehicleAddress && vehicleAddress.suburb) {
    const partialAddress = `${vehicleAddress.suburb}, ${vehicleAddress.state || ''}`.trim()
    return partialAddress
  }
  
  // 5. Dealer location fallback
  const fallbackAddress = dealerInfo.value.location || 'Melbourne, VIC'
  return fallbackAddress
}

// Prevent double view tracking across SSR/client and route changes
const hasTrackedView = ref(false)
async function trackViewOnce() {
  if (hasTrackedView.value || !vehicleStore) return
  try {
    hasTrackedView.value = true
    vehicleStore.addToRecentlyViewed(vehicleId)
    await vehicleStore.incrementViewCount(vehicleId)
  } catch (e) {
    // If tracking fails, allow retry on next call
    hasTrackedView.value = false
    console.warn('[VehicleDetail] view tracking failed:', e)
  }
}

// Defer market intelligence fetch until the browser is idle
function scheduleFetchMarketIntelligence() {
  if (!vehicle.value) return
  const idle = (window as any).requestIdleCallback || ((cb: Function) => setTimeout(cb, 500))
  idle(() => {
    if (!marketIntelligence.value && vehicleMake.value && vehicleModel.value && vehicleYear.value && vehiclePrice.value) {
      fetchMarketIntelligence()
    }
  })
}

// Lazy render control for map/hours section
const mapSectionRef = ref<HTMLElement | null>(null)
const isMapSectionVisible = ref(false)

// SEO Meta Tags
useSeoMeta({
  title: () => vehicle.value ? `${vehicleYear.value} ${vehicleMake.value} ${vehicleModel.value} ${vehicleBadge.value} - ${formatPrice(vehiclePrice.value)}` : 'Vehicle Details',
  description: () => vehicle.value ? `Buy this ${vehicleYear.value} ${vehicleMake.value} ${vehicleModel.value} ${vehicleBadge.value} with ${formatMileage(vehicleKms.value)} km for ${formatPrice(vehiclePrice.value)}. ${vehicleCondition.value} condition, certified dealer.` : 'Vehicle for sale',
  ogTitle: () => vehicle.value ? `${vehicleYear.value} ${vehicleMake.value} ${vehicleModel.value} ${vehicleBadge.value}` : 'Vehicle Details',
  ogDescription: () => vehicle.value ? `${formatPrice(vehiclePrice.value)} • ${formatMileage(vehicleKms.value)} km • ${vehicleCondition.value} condition` : 'Vehicle for sale',
  ogImage: () => vehicleImages.value.length > 0 ? vehicleImages.value[0] : '',
})

// Prepare canonical URL reactively and register via useHead
const runtimeConfig = useRuntimeConfig()
const siteUrl = runtimeConfig.public.siteUrl || ''
const canonicalUrl = computed(() => {
  const idPart = vehicle.value?.stockid || vehicleId
  const slugPart = route.params.slug as string
  return `${siteUrl}/vehicle-for-sale/${idPart}/${slugPart}`
})

useHead(() => ({
  link: [
    { rel: 'canonical', href: canonicalUrl.value }
  ]
}))
</script>

<template>
  <div class="min-h-screen bg-background">
    <!-- Above-the-Fold Zone (Hero Section) -->
    <section class="relative">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <!-- Navigation Breadcrumb -->
        <nav v-if="vehicle" class="flex items-center space-x-2 text-sm text-muted-foreground mb-6">
          <NuxtLink to="/" class="hover:text-primary">
            Home
          </NuxtLink>
          <ChevronRight class="w-4 h-4" />
          <NuxtLink to="/cars-for-sale" class="hover:text-primary">
            Vehicles
          </NuxtLink>
          <ChevronRight class="w-4 h-4" />
         <NuxtLink :to="`/cars-for-sale?make=${vehicleMakeSlug}`" class="hover:text-primary">
            {{ vehicleMake }}
          </NuxtLink>
          <ChevronRight class="w-4 h-4" />
          <span class="text-foreground">{{ vehicleModel }}</span>
        </nav>

        <!-- Hero Section - Two Column Layout (60/40 split) -->
        <div class="flex flex-col lg:flex-row gap-8">
          <!-- Left Column - Image Gallery (60%) -->
          <div class="flex-1 lg:w-2/3">
            <!-- Photo Carousel Slide Component -->
            <div class="relative">
              <PhotoCarouselSlide 
                v-if="vehicleImages.length > 0"
                :photos="vehicleImages"
              />
              
              <!-- View Counter Overlay -->
              <Badge v-if="vehicleViewCount > 0" class="absolute top-4 right-4 bg-black/70 text-white backdrop-blur-sm z-10">
                <Eye class="w-4 h-4 mr-1" />
                {{ vehicleViewCount }} views
              </Badge>
            </div>

            <!-- Vehicle Info Badges - Modern Tesla-inspired Design -->
            <div v-if="vehicle" class="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6">
              <!-- Odometer Badge -->
              <div class="flex flex-col items-center text-center space-y-2">
                <Gauge class="w-6 h-6 text-muted-foreground" />
                <div>
                  <div class="text-sm font-medium text-foreground">{{ formatMileage(vehicleKms) }}km</div>
                  <div class="text-xs text-muted-foreground">Odometer</div>
                </div>
              </div>

              <!-- Body Type Badge -->
              <div class="flex flex-col items-center text-center space-y-2">
                <Car class="w-6 h-6 text-muted-foreground" />
                <div>
                  <div class="text-sm font-medium text-foreground">{{ vehicleBody || 'SUV' }}</div>
                  <div class="text-xs text-muted-foreground">Body type</div>
                </div>
              </div>

              <!-- Fuel Badge -->
              <div class="flex flex-col items-center text-center space-y-2">
                <Fuel class="w-6 h-6 text-muted-foreground" />
                <div>
                  <div class="text-sm font-medium text-foreground">{{ vehicleFuel || 'Petrol' }}</div>
                  <div class="text-xs text-muted-foreground">Fuel</div>
                </div>
              </div>

              <!-- Transmission Badge -->
              <div class="flex flex-col items-center text-center space-y-2">
                <Settings class="w-6 h-6 text-muted-foreground" />
                <div>
                  <div class="text-sm font-medium text-foreground">{{ vehicleTransmission || 'Automatic' }}</div>
                  <div class="text-xs text-muted-foreground">Transmission</div>
                </div>
              </div>
            </div>

            <!-- Dealer Comments Section -->
            <div v-if="vehicle && (vehicle.description || vehicle.Comments)" class="mt-6">
              <Card class="p-6">
                <h3 class="text-lg font-semibold text-foreground mb-4 flex items-center">
                  <MessageSquare class="w-5 h-5 mr-2 text-primary" />
                  Dealer Comments
                </h3>
                <div class="space-y-4">
                  <div v-if="!showFullDescription && vehicleDescription && vehicleDescription.length > 300" class="space-y-4">
                    <!-- Formatted dealer comments with better typography -->
                    <div>
                      <p class="text-foreground leading-relaxed font-medium whitespace-pre-line">
                        {{ formattedDealerDescriptionShort }}...
                      </p>
                    </div>
                    <Button 
                      variant="link" 
                      class="p-0 h-auto text-primary hover:text-primary/80"
                      @click="showFullDescription = true"
                    >
                      Read more
                      <ChevronDown class="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                  <div v-else class="space-y-4">
                    <!-- Full formatted dealer comments -->
                    <div>
                      <p class="text-foreground leading-relaxed font-medium whitespace-pre-line">
                        {{ formattedDealerDescription }}
                      </p>
                    </div>
                    <Button 
                      v-if="vehicleDescription && vehicleDescription.length > 300"
                      variant="link" 
                      class="p-0 h-auto text-primary hover:text-primary/80"
                      @click="showFullDescription = false"
                    >
                      Show less
                      <ChevronUp class="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </Card>
            </div>

            <!-- Australian Vehicle Overview (AI-Generated) -->
            <div v-if="enrichmentData?.description" class="mt-6">
              <Card class="p-6">
                <h3 class="text-lg font-semibold text-foreground mb-4 flex items-center">
                  <div class="w-5 h-5 mr-2 rounded bg-gradient-to-br from-green-500 to-yellow-500 flex items-center justify-center">
                    <span class="text-xs font-bold text-white">AU</span>
                  </div>
                  Australian Market Overview
                </h3>
                <div class="prose prose-sm max-w-none">
                  <div v-if="!showFullAIDescription && enrichmentData.description.length > 300" class="space-y-4">
                    <p class="text-muted-foreground leading-relaxed">
                      {{ enrichmentData.description.substring(0, 300) }}...
                    </p>
                    <Button 
                      variant="link" 
                      class="p-0 h-auto text-primary hover:text-primary/80"
                      @click="showFullAIDescription = true"
                    >
                      Read more
                      <ChevronDown class="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                  <div v-else class="space-y-4">
                    <p class="text-muted-foreground leading-relaxed">
                      {{ enrichmentData.description }}
                    </p>
                    <Button 
                      v-if="enrichmentData.description.length > 300"
                      variant="link" 
                      class="p-0 h-auto text-primary hover:text-primary/80"
                      @click="showFullAIDescription = false"
                    >
                      Show less
                      <ChevronUp class="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                  
                  <!-- Australian AI Badge -->
                  <div class="flex items-center space-x-2 pt-2 border-t border-border/50">
                    <div class="w-4 h-4 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                      <Bot class="w-3 h-3 text-purple-600 dark:text-purple-400" />
                    </div>
                    <span class="text-xs text-muted-foreground">
                      AI-generated Australian market analysis
                    </span>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          <!-- Right Column - Vehicle Info & Actions (40%) -->
          <div class="w-full lg:w-1/3 lg:flex-shrink-0">
            <div class="sticky top-20 space-y-6">
            <!-- Trust Indicators -->
            <div class="flex items-center space-x-4 text-sm">
              <Badge variant="secondary" class="text-green-600">
                <ShieldCheck class="w-4 h-4 mr-1" />
                {{ vehicleCondition }}
              </Badge>
              <Badge variant="secondary" class="text-blue-600">
                <Award class="w-4 h-4 mr-1" />
                Quality Assured
              </Badge>
            </div>

            <!-- Vehicle Identity Header -->
            <div v-if="vehicle" class="flex items-start justify-between gap-4">
              <div class="flex-1">
                <h1 class="text-3xl font-bold text-foreground mb-2">
                  {{ vehicle.title || `${vehicleYear} ${vehicleMake} ${vehicleModel}` }}
                </h1>
              </div>
              <FavoriteButton :vehicle-id="vehicle.id" />
            </div>

            <!-- Modern Tesla-Inspired Price Display -->
            <div v-if="vehicle" class="space-y-6">
              <!-- Primary Price Card -->
              <div class="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-800 border border-slate-200 dark:border-slate-700">
                <div class="p-8">
                  <!-- Price Header -->
                  <div class="mb-6">
                    <div class="text-4xl font-bold tracking-tight text-slate-900 dark:text-white mb-1">
                      {{ formatPrice(vehiclePrice) }}
                    </div>
                    <div class="text-sm font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                      Vehicle Price
                    </div>
                  </div>

                  <!-- Market Analysis Section -->
                  <div class="space-y-4">
                    <!-- Dynamic Market Analysis -->
                    <div v-if="enrichmentData && enrichmentData.marketData && enrichmentData.marketData.averagePrice > 0" class="space-y-3">
                      <div class="flex items-center space-x-3 p-4 rounded-xl bg-slate-100/50 dark:bg-slate-800/50">
                        <div class="flex-shrink-0">
                          <div class="w-8 h-8 rounded-full flex items-center justify-center"
                               :class="{
                                 'bg-green-100 dark:bg-green-900': marketPositionIcon === 'lucide:trending-down',
                                 'bg-orange-100 dark:bg-orange-900': marketPositionIcon === 'lucide:trending-up',
                                 'bg-blue-100 dark:bg-blue-900': marketPositionIcon === 'lucide:minus'
                               }">
                            <TrendingDown 
                              class="w-4 h-4"
                              :class="{
                                'text-green-600 dark:text-green-400': marketPositionIcon === 'lucide:trending-down',
                                'text-orange-600 dark:text-orange-400': marketPositionIcon === 'lucide:trending-up',
                                'text-blue-600 dark:text-blue-400': marketPositionIcon === 'lucide:minus'
                              }"
                            />
                          </div>
                        </div>
                        <div class="flex-1 min-w-0">
                          <div class="text-sm font-medium text-slate-900 dark:text-white">
                            {{ marketPositionText }}
                          </div>
                          <div class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                            Market average: {{ formatPrice(enrichmentData.marketData.averagePrice) }}
                            <span v-if="enrichmentData.marketData.trend !== 'stable'" class="ml-2 px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-xs">
                              {{ enrichmentData.marketData.trend }}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <!-- Data Source -->
                      <div class="flex items-center justify-between text-xs text-slate-400 dark:text-slate-500">
                        <span>Market Analysis</span>
                        <span>{{ enrichmentData.marketData.confidence }} confidence • {{ enrichmentData.marketData.dataSource }}</span>
                      </div>
                    </div>
                    
                    <!-- Fallback Market Context -->
                    <div v-else-if="!isLoadingEnrichment" class="flex items-center space-x-3 p-4 rounded-xl bg-slate-100/50 dark:bg-slate-800/50">
                      <div class="flex-shrink-0">
                        <div class="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                          <TrendingDown class="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </div>
                      </div>
                      <div class="flex-1">
                        <div class="text-sm font-medium text-slate-900 dark:text-white">
                          Competitive market pricing
                        </div>
                        <div class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                          Price analysis in progress
                        </div>
                      </div>
                    </div>
                    
                    <!-- Loading State -->
                    <div v-else class="flex items-center space-x-3 p-4 rounded-xl bg-slate-100/50 dark:bg-slate-800/50">
                      <div class="flex-shrink-0">
                        <div class="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                          <Loader2 class="w-4 h-4 text-slate-500 animate-spin" />
                        </div>
                      </div>
                      <div class="flex-1">
                        <div class="text-sm font-medium text-slate-900 dark:text-white">
                          Analyzing market position
                        </div>
                        <div class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                          Comparing with similar vehicles...
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Payment Information Footer -->
                <div class="border-t border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 px-8 py-4">
                  <div class="flex items-center justify-between">
                    <div class="text-sm text-slate-600 dark:text-slate-400">
                      Est. monthly payment
                    </div>
                    <div class="text-lg font-semibold text-slate-900 dark:text-white">
                      ${{ calculateMonthlyPayment(vehiclePrice) }}<span class="text-sm font-normal text-slate-500">/month</span>
                    </div>
                  </div>
                  <div class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Based on 5% down, 6% APR, 60 months
                  </div>
                </div>
              </div>

              <!-- Quick Finance Options -->
              <div class="grid grid-cols-3 gap-3">
                <button 
                  @click="handleFinanceClick"
                  class="group relative overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 text-left transition-all hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-sm"
                >
                  <div class="flex flex-col items-center space-y-2">
                    <div class="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                      <Calculator class="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div class="text-center">
                      <div class="text-sm font-medium text-slate-900 dark:text-white">
                        Finance
                      </div>
                      <div class="text-xs text-slate-500 dark:text-slate-400">
                        Pre-approved
                      </div>
                    </div>
                  </div>
                </button>

                <button 
                  @click="showAIChatDialog = true"
                  class="group relative overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 text-left transition-all hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-sm"
                >
                  <div class="flex flex-col items-center space-y-2">
                    <div class="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                      <Bot class="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div class="text-center">
                      <div class="text-sm font-medium text-slate-900 dark:text-white">
                        AI Chat
                      </div>
                      <div class="text-xs text-slate-500 dark:text-slate-400">
                        Ask anything
                      </div>
                    </div>
                  </div>
                </button>

                <button 
                  @click="handleInquiryClick"
                  class="group relative overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 text-left transition-all hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-sm"
                >
                  <div class="flex flex-col items-center space-y-2">
                    <div class="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                      <HelpCircle class="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div class="text-center">
                      <div class="text-sm font-medium text-slate-900 dark:text-white">
                        Inquire
                      </div>
                      <div class="text-xs text-slate-500 dark:text-slate-400">
                        Contact us
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            </div>

            <!-- Primary Action Trigger -->
            <div class="space-y-3">
              <Button
                size="lg"
                class="w-full"
                @click="handlePrimaryAction"
              >
                <Calendar class="w-5 h-5 mr-2" />
                Schedule Test Drive
              </Button>

              <div class="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  @click="handleCall"
                >
                  <Phone class="w-4 h-4 mr-2" />
                  Call Now
                </Button>
                <Button
                  variant="outline"
                  @click="handleMessage"
                >
                  <MessageCircle class="w-4 h-4 mr-2" />
                  Message
                </Button>
              </div>
            </div>

            <!-- Dealer Information -->
            <Card class="p-4">
              <h3 class="font-semibold text-foreground mb-3">
                Dealer Information
              </h3>
              <div class="flex items-center space-x-3 mb-4">
                <img :src="dealerInfo.logo" :alt="dealerInfo.name" class="w-10 h-10 rounded-full">
                <div class="flex-1">
                  <div class="font-medium text-foreground">
                    {{ dealerInfo.name }}
                  </div>
                  <div class="text-sm text-muted-foreground">
                    {{ dealerInfo.location }}
                  </div>
                </div>
              </div>

              <!-- Contact Information -->
              <div class="space-y-3 mb-4">
                <!-- Phone Number -->
                <div v-if="dealerInfo.phone" class="flex items-center space-x-2">
                  <Phone class="w-4 h-4 text-muted-foreground" />
                  <a 
                    :href="`tel:${dealerInfo.phone}`"
                    class="text-sm text-foreground hover:text-primary transition-colors"
                  >
                    {{ dealerInfo.phone }}
                  </a>
                </div>

                <!-- Address with Directions -->
                <div class="flex items-start space-x-2">
                  <MapPin class="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div class="flex-1">
                    <div class="text-sm text-foreground mb-1">
                      {{ getFullAddress() }}
                    </div>
                    <button
                      @click="openDirections"
                      class="inline-flex items-center space-x-1 text-xs text-primary hover:text-primary/80 transition-colors"
                    >
                      <Navigation class="w-3 h-3" />
                      <span>Get Directions</span>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Rating and Hours -->
              <div class="flex items-center space-x-4 text-sm text-muted-foreground">
                <div class="flex items-center space-x-1">
                  <Star class="w-4 h-4 text-yellow-400" />
                  <span>{{ dealerInfo.rating }} ({{ dealerInfo.reviewCount }} reviews)</span>
                </div>
                <div class="flex items-center space-x-1">
                  <Clock class="w-4 h-4" />
                  <span>Open until {{ dealerInfo.closingTime }}</span>
                </div>
              </div>
            </Card>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Secondary Engagement Zone -->
    <section class="bg-muted/50 py-12">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <!-- Primary Vehicle Specifications -->
          <Card v-if="vehicle" class="p-6">
            <h3 class="text-lg font-semibold text-foreground mb-4">
              Vehicle Overview
            </h3>
            <dl class="space-y-3">
              <!-- Year -->
              <div v-if="vehicleYear" class="grid grid-cols-2 gap-4 items-start">
                <dt class="text-muted-foreground">
                  Year
                </dt>
                <dd class="font-medium text-right">
                  {{ vehicleYear }}
                </dd>
              </div>
              
              <!-- Make & Model -->
              <div v-if="vehicleMake && vehicleModel" class="grid grid-cols-2 gap-4 items-start">
                <dt class="text-muted-foreground">
                  Make & Model
                </dt>
                <dd class="font-medium text-right">
                  {{ vehicleMake }} {{ vehicleModel }}
                </dd>
              </div>
              
              <!-- Badge/Variant -->
              <div v-if="vehicleBadge" class="grid grid-cols-2 gap-4 items-start">
                <dt class="text-muted-foreground">
                  Variant
                </dt>
                <dd class="font-medium text-right">
                  {{ vehicleBadge }}
                </dd>
              </div>
              
              <!-- Body Style -->
              <div v-if="vehicleBody" class="grid grid-cols-2 gap-4 items-start">
                <dt class="text-muted-foreground">
                  Body Style
                </dt>
                <dd class="font-medium text-right">
                  {{ vehicleBody }}
                </dd>
              </div>
              
              <!-- Condition -->
              <div v-if="vehicleCondition" class="grid grid-cols-2 gap-4 items-start">
                <dt class="text-muted-foreground">
                  Condition
                </dt>
                <dd class="font-medium text-right">
                  {{ vehicleCondition }}
                </dd>
              </div>
              
              <!-- Odometer -->
              <div class="grid grid-cols-2 gap-4 items-start">
                <dt class="text-muted-foreground">
                  Odometer
                </dt>
                <dd class="font-medium text-right">
                  {{ formatMileage(vehicleKms) }} km
                </dd>
              </div>
              
              <!-- Seating -->
              <div v-if="vehicleSeats" class="grid grid-cols-2 gap-4 items-start">
                <dt class="text-muted-foreground">
                  Seating
                </dt>
                <dd class="font-medium text-right">
                  {{ vehicleSeats }} passengers
                </dd>
              </div>
              
              <!-- Doors -->
              <div v-if="vehicleDoors" class="grid grid-cols-2 gap-4 items-start">
                <dt class="text-muted-foreground">
                  Doors
                </dt>
                <dd class="font-medium text-right">
                  {{ vehicleDoors }}
                </dd>
              </div>
            </dl>
          </Card>

          <!-- Engine & Performance Specifications -->
          <Card v-if="vehicle" class="p-6">
            <h3 class="text-lg font-semibold text-foreground mb-4">
              Engine & Performance
            </h3>
            <dl class="space-y-3">
              <!-- Engine -->
              <div v-if="vehicleEngine" class="grid grid-cols-2 gap-4 items-start">
                <dt class="text-muted-foreground">
                  Engine
                </dt>
                <dd class="font-medium text-right">
                  {{ vehicleEngine }}
                </dd>
              </div>
              
              <!-- Transmission -->
              <div v-if="vehicleTransmission" class="grid grid-cols-2 gap-4 items-start">
                <dt class="text-muted-foreground">
                  Transmission
                </dt>
                <dd class="font-medium text-right">
                  {{ vehicleTransmission }}
                </dd>
              </div>
              
              <!-- Gears (if available) -->
              <div v-if="vehicle.gears" class="grid grid-cols-2 gap-4 items-start">
                <dt class="text-muted-foreground">
                  Gears
                </dt>
                <dd class="font-medium text-right">
                  {{ vehicle.gears }} Speed
                </dd>
              </div>
              
              <!-- Drivetrain -->
              <div v-if="vehicleDrivetrain" class="grid grid-cols-2 gap-4 items-start">
                <dt class="text-muted-foreground">
                  Drivetrain
                </dt>
                <dd class="font-medium text-right">
                  {{ vehicleDrivetrain }}
                </dd>
              </div>
              
              <!-- Fuel Type -->
              <div v-if="vehicleFuel" class="grid grid-cols-2 gap-4 items-start">
                <dt class="text-muted-foreground">
                  Fuel Type
                </dt>
                <dd class="font-medium text-right">
                  {{ vehicleFuel }}
                </dd>
              </div>
            </dl>
          </Card>

          <!-- Vehicle Details & Documentation -->
          <Card v-if="vehicle" class="p-6">
            <h3 class="text-lg font-semibold text-foreground mb-4">
              Details & Documentation
            </h3>
            <dl class="space-y-3">
              <!-- VIN (if available) -->
              <div v-if="vehicle.vin" class="grid grid-cols-2 gap-4 items-start">
                <dt class="text-muted-foreground">
                  VIN
                </dt>
                <dd class="font-medium text-xs text-right">
                  {{ vehicle.vin }}
                </dd>
              </div>
              
              <!-- Registration (if available) -->
              <div v-if="vehicle.rego" class="grid grid-cols-2 gap-4 items-start">
                <dt class="text-muted-foreground">
                  Registration
                </dt>
                <dd class="font-medium text-right">
                  {{ vehicle.rego }}
                </dd>
              </div>
              
              <!-- Registration Expiry (if available) -->
              <div v-if="vehicle.regoexpiry && vehicle.regoexpiry !== 'N/A'" class="grid grid-cols-2 gap-4 items-start">
                <dt class="text-muted-foreground">
                  Rego Expiry
                </dt>
                <dd class="font-medium text-right">
                  {{ vehicle.regoexpiry }}
                </dd>
              </div>
              
              <!-- Exterior Color -->
              <div v-if="vehicle.genericolour || (vehicle.colour && vehicle.colour.displayValue && vehicle.colour.displayValue[0])" class="grid grid-cols-2 gap-4 items-start">
                <dt class="text-muted-foreground">
                  Exterior Color
                </dt>
                <dd class="font-medium text-right">
                  {{ vehicle.genericolour || vehicle.colour?.displayValue?.[0] }}
                </dd>
              </div>
              
              <!-- Interior Color -->
              <div v-if="vehicle.interiorcolour" class="grid grid-cols-2 gap-4 items-start">
                <dt class="text-muted-foreground">
                  Interior
                </dt>
                <dd class="font-medium text-right">
                  {{ vehicle.interiorcolour }}
                </dd>
              </div>
              
              <!-- Build Month (if available) -->
              <div v-if="vehicle.buildmonth && vehicle.buildmonth !== 'N/A'" class="grid grid-cols-2 gap-4 items-start">
                <dt class="text-muted-foreground">
                  Build Month
                </dt>
                <dd class="font-medium text-right">
                  {{ vehicle.buildmonth }}
                </dd>
              </div>
              
              <!-- Compliance Year (if available) -->
              <div v-if="vehicle.complianceyear && vehicle.complianceyear !== 'N/A'" class="grid grid-cols-2 gap-4 items-start">
                <dt class="text-muted-foreground">
                  Compliance Year
                </dt>
                <dd class="font-medium text-right">
                  {{ vehicle.complianceyear }}
                </dd>
              </div>
              
              <!-- Series (if available) -->
              <div v-if="vehicle.series && vehicle.series.displayValue && vehicle.series.displayValue[0] !== '(No Series)'" class="grid grid-cols-2 gap-4 items-start">
                <dt class="text-muted-foreground">
                  Series
                </dt>
                <dd class="font-medium text-right">
                  {{ vehicle.series.displayValue[0] }}
                </dd>
              </div>
              
              <!-- Stock ID -->
              <div v-if="vehicle.stockid" class="grid grid-cols-2 gap-4 items-start">
                <dt class="text-muted-foreground">
                  Stock ID
                </dt>
                <dd class="font-medium text-right">
                  {{ vehicle.stockid }}
                </dd>
              </div>
            </dl>
          </Card>

          <!-- Financing Preview -->
          <Card class="p-6">
            <h3 class="text-lg font-semibold text-foreground mb-4">
              Financing Options
            </h3>
            <div class="space-y-4">
              <div class="p-4 bg-green-50 dark:bg-green-950 rounded-lg">
                <div class="text-green-800 dark:text-green-200 font-semibold">
                  Best Rate Available
                </div>
                <div class="text-2xl font-bold text-green-900 dark:text-green-100">
                  3.9% APR
                </div>
                <div class="text-sm text-green-700 dark:text-green-300">
                  With approved credit
                </div>
              </div>
              <Button class="w-full">
                Get Pre-Approved
              </Button>
              <Button variant="outline" class="w-full">
                Calculate Payments
              </Button>
            </div>
          </Card>

            <!-- ANCAP Safety Rating Component -->
            <VehicleANCAPRating 
              v-if="vehicle"
              :make="vehicleMake"
              :model="vehicleModel"
              :year="parseInt(vehicleYear)"
              :vehicle-id="vehicleId"
            />

            <!-- Vehicle History Summary -->
            <Card class="p-6">
              <h3 class="text-lg font-semibold text-foreground mb-4">
                Vehicle History
              </h3>
              <div class="space-y-3">
                <div class="flex items-center space-x-3">
                  <div class="w-6 h-6 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                    <Check class="w-4 h-4 text-green-600" />
                  </div>
                  <span class="text-sm">No accidents reported</span>
                </div>
                <div class="flex items-center space-x-3">
                  <div class="w-6 h-6 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                    <Check class="w-4 h-4 text-green-600" />
                  </div>
                  <span class="text-sm">{{ vehicle?.previousOwners || 1 }} previous owner</span>
                </div>
                <div class="flex items-center space-x-3">
                  <div class="w-6 h-6 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                    <Check class="w-4 h-4 text-green-600" />
                  </div>
                  <span class="text-sm">Service records available</span>
                </div>
                <Button variant="link" class="p-0 h-auto text-sm">
                  View full CarFax report →
                </Button>
              </div>
            </Card>
        </div>
      </div>
    </section>

    <!-- Deep Engagement Zone -->
    <section class="py-12">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Tab Content Grid -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <!-- Main Content Area -->
          <div class="lg:col-span-2">
            <!-- Shadcn Tabs -->
            <Tabs :default-value="defaultTabValue" class="w-full">
              <TabsList class="grid w-full grid-cols-4 mb-8">
                <TabsTrigger 
                  v-for="tab in tabsConfig"
                  :key="tab.id"
                  :value="tab.id"
                >
                  {{ tab.name }}
                </TabsTrigger>
              </TabsList>

              <!-- Features Tab -->
              <TabsContent value="features" class="space-y-6">
                <h2 class="text-2xl font-bold text-foreground">
                  Features & Equipment
                </h2>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div v-for="category in featureCategories" :key="category.name" class="space-y-3">
                    <h4 class="font-semibold text-foreground flex items-center space-x-2">
                      <Shield :class="category.icon === 'lucide:shield' ? 'w-5 h-5 text-primary' : 'hidden'" />
                      <span>{{ category.name }}</span>
                    </h4>
                    <ul class="space-y-2">
                      <li v-for="feature in category.features" :key="feature" class="flex items-center space-x-2">
                        <Check class="w-4 h-4 text-green-600" />
                        <span class="text-sm text-muted-foreground">{{ feature }}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </TabsContent>

              <!-- Overview Tab -->
              <TabsContent value="overview" class="space-y-6">
                <h2 class="text-2xl font-bold text-foreground">
                  Vehicle Overview
                </h2>

                <!-- Dealer Comments Section -->
                <div v-if="vehicle && (vehicle.description || vehicle.Comments)" class="mt-6">
                  <Card class="p-6">
                    <h3 class="text-lg font-semibold text-foreground mb-4 flex items-center">
                      <MessageSquare class="w-5 h-5 mr-2 text-primary" />
                      Dealer Comments
                    </h3>
                  <div class="space-y-4">
                    <div v-if="!showFullDescription && vehicleDescription && vehicleDescription.length > 300" class="space-y-4">
                      <!-- Formatted dealer comments with better typography -->
                      <div>
                        <p class="text-foreground leading-relaxed font-medium whitespace-pre-line">
                          {{ formattedDealerDescriptionShort }}...
                        </p>
                      </div>
                      <Button 
                        variant="link" 
                        class="p-0 h-auto text-primary hover:text-primary/80"
                        @click="showFullDescription = true"
                      >
                        Read more
                        <ChevronDown class="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                    <div v-else class="space-y-4">
                      <!-- Full formatted dealer comments -->
                      <div>
                        <p class="text-foreground leading-relaxed font-medium whitespace-pre-line">
                          {{ formattedDealerDescription }}
                        </p>
                      </div>
                      <Button 
                        v-if="vehicleDescription && vehicleDescription.length > 300"
                        variant="link" 
                        class="p-0 h-auto text-primary hover:text-primary/80"
                        @click="showFullDescription = false"
                      >
                        Show less
                        <ChevronUp class="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>

              <!-- Australian Vehicle Overview (AI-Generated) -->
              <div v-if="enrichmentData?.description" class="mt-6">
                <Card class="p-6">
                  <h3 class="text-lg font-semibold text-foreground mb-4 flex items-center">
                    <div class="w-5 h-5 mr-2 rounded bg-gradient-to-br from-green-500 to-yellow-500 flex items-center justify-center">
                      <span class="text-xs font-bold text-white">AU</span>
                    </div>
                    Australian Market Overview
                  </h3>
                  <div class="prose prose-sm max-w-none">
                    <div v-if="!showFullAIDescription && enrichmentData.description.length > 300" class="space-y-4">
                      <p class="text-muted-foreground leading-relaxed">
                        {{ enrichmentData.description.substring(0, 300) }}...
                      </p>
                      <Button 
                        variant="link" 
                        class="p-0 h-auto text-primary hover:text-primary/80"
                        @click="showFullAIDescription = true"
                      >
                        Read more
                        <ChevronDown class="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                    <div v-else class="space-y-4">
                      <p class="text-muted-foreground leading-relaxed">
                        {{ enrichmentData.description }}
                      </p>
                      <Button 
                        v-if="enrichmentData.description.length > 300"
                        variant="link" 
                        class="p-0 h-auto text-primary hover:text-primary/80"
                        @click="showFullAIDescription = false"
                      >
                        Show less
                        <ChevronUp class="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                    
                    <!-- Australian AI Badge -->
                    <div class="flex items-center space-x-2 pt-2 border-t border-border/50">
                      <div class="w-4 h-4 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                        <Bot class="w-3 h-3 text-purple-600 dark:text-purple-400" />
                      </div>
                      <span class="text-xs text-muted-foreground">
                        AI-generated Australian market analysis
                      </span>
                    </div>
                  </div>
                </Card>
              </div>

              <!-- Vehicle Highlights -->
              <Card class="p-6">
                <h3 class="text-lg font-semibold text-foreground mb-4">
                  Vehicle Highlights
                </h3>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div class="flex items-center space-x-3">
                    <div class="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                      <Check class="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <div class="font-medium text-foreground">{{ vehicle?.previousOwners || 1 }} Previous Owner{{ (vehicle?.previousOwners || 1) > 1 ? 's' : '' }}</div>
                      <div class="text-sm text-muted-foreground">{{ Math.round((new Date().getFullYear() - parseInt(vehicleYear)) / (vehicle?.previousOwners || 1)) }} years average ownership</div>
                    </div>
                  </div>
                  <div class="flex items-center space-x-3">
                    <div class="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                      <Check class="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <div class="font-medium text-foreground">{{ getUsageClassification() }}</div>
                      <div class="text-sm text-muted-foreground">{{ Math.round(vehicleKms / (new Date().getFullYear() - parseInt(vehicleYear))) || 'N/A' }} km/year average</div>
                    </div>
                  </div>
                  <div v-if="serviceHistory.length > 0" class="flex items-center space-x-3">
                    <div class="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                      <Check class="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <div class="font-medium text-foreground">Service History Available</div>
                      <div class="text-sm text-muted-foreground">{{ serviceHistory.length }} documented services</div>
                    </div>
                  </div>
                  <div v-else class="flex items-center space-x-3">
                    <div class="w-8 h-8 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center">
                      <HelpCircle class="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <div class="font-medium text-foreground">Service History</div>
                      <div class="text-sm text-muted-foreground">Contact us for Records</div>
                    </div>
                  </div>
                  <div v-if="vehicle?.ppsr?.status === 'clear'" class="flex items-center space-x-3">
                    <div class="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                      <Check class="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <div class="font-medium text-foreground">PPSR Clear</div>
                      <div class="text-sm text-muted-foreground">No finance owing, verified</div>
                    </div>
                  </div>
                  <div v-else-if="vehicle?.ppsr?.status" class="flex items-center space-x-3">
                    <div class="w-8 h-8 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                      <HelpCircle class="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <div class="font-medium text-foreground">PPSR Issues</div>
                      <div class="text-sm text-muted-foreground">Contact us for Verification</div>
                    </div>
                  </div>
                  <div v-else class="flex items-center space-x-3">
                    <div class="w-8 h-8 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center">
                      <HelpCircle class="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <div class="font-medium text-foreground">PPSR Status</div>
                      <div class="text-sm text-muted-foreground">Contact us for Verification</div>
                    </div>
                  </div>
                </div>
              </Card>
            </TabsContent>

            <!-- Specifications Tab -->
            <TabsContent value="specifications" class="space-y-6">
              <h2 class="text-2xl font-bold text-foreground">
                Complete Specifications
              </h2>

              <!-- Vehicle Specifications Accordion -->
              <VehicleSpecificationsAccordion 
                :specifications="enrichmentData?.specifications || {}"
              />
            </TabsContent>

            <!-- History Tab -->
            <TabsContent value="history" class="space-y-8">
              <h2 class="text-2xl font-bold text-foreground">
                Market Analysis
              </h2>

              <!-- Market Intelligence Widget -->
              <div v-if="marketIntelligence || isLoadingMarketIntelligence" class="mb-8">
                <Card class="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
                  <div class="flex items-center justify-between mb-6">
                    <div class="flex items-center space-x-3">
                      <div class="flex items-center justify-center w-12 h-12 bg-blue-600 rounded-lg">
                        <TrendingUp class="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 class="text-xl font-bold text-foreground">Live Market Intelligence</h3>
                        <p class="text-muted-foreground">Real-time Australian market analysis</p>
                      </div>
                    </div>
                    <Badge v-if="marketIntelligence" class="flex items-center space-x-2 px-3 py-1.5">
                      <div class="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span>Live Data</span>
                    </Badge>
                  </div>

                  <!-- Loading State -->
                  <div v-if="isLoadingMarketIntelligence" class="text-center py-8">
                    <Loader2 class="w-8 h-8 mx-auto animate-spin text-blue-600 mb-4" />
                    <p class="text-muted-foreground">Analyzing current market conditions...</p>
                  </div>

                  <!-- Market Intelligence Results -->
                  <div v-else-if="marketIntelligence" class="space-y-6">
                    <!-- Quick Stats Grid -->
                    <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div class="bg-white/60 dark:bg-gray-800/60 rounded-lg p-4">
                        <div class="flex items-center justify-between mb-2">
                          <DollarSign class="w-5 h-5 text-green-600" />
                          <span class="text-xs text-muted-foreground">Market Position</span>
                        </div>
                        <div class="text-xl font-bold" :class="marketIntelligence.marketPosition.position === 'below' ? 'text-green-600' : marketIntelligence.marketPosition.position === 'above' ? 'text-red-600' : 'text-blue-600'">
                          {{ marketIntelligence.marketPosition.position === 'below' ? 'Below Market' : marketIntelligence.marketPosition.position === 'above' ? 'Above Market' : 'At Market' }}
                        </div>
                        <div class="text-xs text-muted-foreground">{{ marketIntelligence.marketPosition.percentile }}th percentile</div>
                      </div>
                      
                      <div class="bg-white/60 dark:bg-gray-800/60 rounded-lg p-4">
                        <div class="flex items-center justify-between mb-2">
                          <Calendar class="w-5 h-5 text-blue-600" />
                          <span class="text-xs text-muted-foreground">Market Demand</span>
                        </div>
                        <div class="text-xl font-bold text-blue-600">{{ marketIntelligence.priceInsights.marketDemand.toUpperCase() }}</div>
                        <div class="text-xs text-muted-foreground">{{ marketIntelligence.priceInsights.avgDaysOnMarket }} days avg</div>
                      </div>
                      
                      <div class="bg-white/60 dark:bg-gray-800/60 rounded-lg p-4">
                        <div class="flex items-center justify-between mb-2">
                          <BarChart class="w-5 h-5 text-orange-600" />
                          <span class="text-xs text-muted-foreground">Negotiation</span>
                        </div>
                        <div class="text-xl font-bold" :class="marketIntelligence.recommendations.negotiationPotential === 'high' ? 'text-green-600' : marketIntelligence.recommendations.negotiationPotential === 'low' ? 'text-red-600' : 'text-orange-600'">
                          {{ marketIntelligence.recommendations.negotiationPotential.toUpperCase() }}
                        </div>
                        <div class="text-xs text-muted-foreground">Potential</div>
                      </div>
                      
                      <div class="bg-white/60 dark:bg-gray-800/60 rounded-lg p-4">
                        <div class="flex items-center justify-between mb-2">
                          <Clock class="w-5 h-5 text-purple-600" />
                          <span class="text-xs text-muted-foreground">Confidence</span>
                        </div>
                        <div class="text-xl font-bold text-purple-600">{{ Math.round(marketIntelligence.confidence * 100) }}%</div>
                        <div class="text-xs text-muted-foreground">Analysis accuracy</div>
                      </div>
                    </div>

                    <!-- AI Recommendations -->
                    <div class="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg p-6">
                      <div class="flex items-start space-x-4">
                        <div class="flex items-center justify-center w-10 h-10 bg-green-600 rounded-lg flex-shrink-0">
                          <Bot class="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h4 class="text-lg font-semibold text-green-800 dark:text-green-200 mb-2">
                            AI Market Recommendations
                          </h4>
                          <div class="space-y-2 text-sm text-green-700 dark:text-green-300">
                            <div class="flex items-start space-x-2">
                              <div class="w-2 h-2 bg-green-600 rounded-full mt-1.5 flex-shrink-0"></div>
                              <div>
                                <div class="font-medium">Pricing Analysis</div>
                                <div>{{ marketIntelligence.recommendations.pricingAdvice }}</div>
                              </div>
                            </div>
                            <div class="flex items-start space-x-2">
                              <div class="w-2 h-2 bg-green-600 rounded-full mt-1.5 flex-shrink-0"></div>
                              <div>
                                <div class="font-medium">Market Timing</div>
                                <div>{{ marketIntelligence.recommendations.marketTiming }}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Data Sources Footer -->
                    <div class="text-xs text-muted-foreground bg-muted/20 rounded-lg p-3">
                      <div class="font-medium mb-1">Data Sources:</div>
                      <div class="grid grid-cols-2 md:grid-cols-3 gap-2">
                        <div>🏛️ Australian Market Data</div>
                        <div>🚗 Live Vehicle Pricing</div>
                        <div>🤖 AI Analysis Engine</div>
                      </div>
                      <div class="mt-2 text-center">
                        Last updated: {{ new Date(marketIntelligence.lastUpdated).toLocaleString('en-AU') }}
                      </div>
                    </div>
                  </div>

                  <!-- Error State -->
                  <div v-else-if="marketIntelligenceError" class="text-center py-8">
                    <HelpCircle class="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <h4 class="font-semibold text-foreground mb-2">Market Data Unavailable</h4>
                    <p class="text-muted-foreground">{{ marketIntelligenceError }}</p>
                  </div>
                </Card>
              </div>

              <!-- Market Analysis & Reports -->
              <Card class="p-6">
                <h3 class="text-xl font-semibold text-foreground mb-6 flex items-center">
                  <BarChart class="w-5 h-5 mr-2 text-indigo-600" />
                  Market Analysis
                </h3>
                
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <!-- Current Market Position -->
                  <div class="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-6">
                    <h4 class="font-semibold text-foreground mb-4">Market Position</h4>
                    <div class="space-y-3">
                      <div class="flex justify-between items-center">
                        <span class="text-sm text-muted-foreground">Market Range</span>
                        <span class="font-medium">${{ formatPrice(vehiclePrice * 0.85).replace('$', '') }} - ${{ formatPrice(vehiclePrice * 1.15).replace('$', '') }}</span>
              </div>
                      <div class="flex justify-between items-center">
                        <span class="text-sm text-muted-foreground">This Vehicle</span>
                        <span class="font-bold text-primary">{{ formatPrice(vehiclePrice) }}</span>
                      </div>
                      <div class="flex justify-between items-center">
                        <span class="text-sm text-muted-foreground">Market Position</span>
                        <Badge class="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Excellent Value</Badge>
                      </div>
                      <div class="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2 mt-4">
                        <div class="bg-green-500 h-2 rounded-full w-3/4"></div>
                      </div>
                      <p class="text-xs text-muted-foreground">Priced competitively within market range</p>
                    </div>
                  </div>

                  <!-- Depreciation Forecast -->
                  <div class="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg p-6">
                    <h4 class="font-semibold text-foreground mb-4">Depreciation Forecast</h4>
                    <div class="space-y-3">
                      <div class="flex justify-between">
                        <span class="text-sm text-muted-foreground">Current Value</span>
                        <span class="font-medium">{{ formatPrice(vehiclePrice) }}</span>
                      </div>
                      <div class="flex justify-between">
                        <span class="text-sm text-muted-foreground">1 Year</span>
                        <span class="font-medium">{{ formatPrice(vehiclePrice * 0.85) }}</span>
                      </div>
                      <div class="flex justify-between">
                        <span class="text-sm text-muted-foreground">3 Years</span>
                        <span class="font-medium">{{ formatPrice(vehiclePrice * 0.65) }}</span>
                      </div>
                      <div class="flex justify-between">
                        <span class="text-sm text-muted-foreground">5 Years</span>
                        <span class="font-medium">{{ formatPrice(vehiclePrice * 0.45) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
              </TabsContent>
            </Tabs>

            <!-- Map and Trading Hours Section -->
            <div class="mt-8" ref="mapSectionRef">
              <DealerMapAndHours
                v-if="isReady && isMapSectionVisible"
                :dealer-name="dealerInfo.name"
                :department="currentDepartment"
                :dealer-address="getMapAddress()"
                :dealer-phone="currentDepartment?.phone || dealerInfo.phone"
                :dealer-email="'contact@dealer.com'"
              />
              
              <!-- Loading state while store initializes or until visible -->
              <div v-else class="bg-background border border-border rounded-lg p-6">
                <div class="animate-pulse">
                  <div class="h-6 bg-muted rounded w-48 mb-4"></div>
                  <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div class="h-64 bg-muted rounded"></div>
                    <div class="space-y-4">
                      <div class="h-4 bg-muted rounded w-3/4"></div>
                      <div class="h-4 bg-muted rounded w-1/2"></div>
                      <div class="h-4 bg-muted rounded w-2/3"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Sidebar - Persistent Contact Panel -->
          <div class="lg:col-span-1">
            <Card class="p-6 sticky top-8">
              <!-- Vehicle Summary Card -->
              <div v-if="vehicle" class="mb-6 p-4 bg-muted/50 rounded-lg border">
                <div class="flex items-start space-x-3">
                  <!-- Vehicle Image -->
                  <div class="flex-shrink-0">
                    <img 
                      :src="vehicleImages.length > 0 ? vehicleImages[0] : 'https://placehold.co/80x60?text=No+Image'"
                      :alt="`${vehicleYear} ${vehicleMake} ${vehicleModel}`"
                      class="w-24 h-16 object-cover rounded-md"
                    >
                  </div>
                  
                  <!-- Vehicle Info -->
                  <div class="flex-1 min-w-0">
                    <h4 class="font-semibold text-sm text-foreground line-clamp-2">
                      {{ vehicle.title || `${vehicleYear} ${vehicleMake} ${vehicleModel} ${vehicleBadge}`.trim() }}
                    </h4>

                    <div class="text-lg font-bold text-primary">
                      {{ formatPrice(vehiclePrice) }}
                    </div>
                  </div>
                </div>

              </div>

              <h3 class="text-lg font-semibold text-foreground mb-4">
                Contact Dealer
              </h3>

              <!-- Quick Contact Actions -->
              <div class="space-y-3 mb-6">
                <Button class="w-full" @click="handleCall">
                  <Phone class="w-4 h-4 mr-2" />
                  {{ dealerInfo.phone }}
                </Button>
                <Button variant="outline" class="w-full" @click="handleMessage">
                  <MessageCircle class="w-4 h-4 mr-2" />
                  Send Message
                </Button>
              </div>

              <!-- Lead Capture Form -->
              <form class="space-y-4" @submit.prevent="submitInquiry">
                <div>
                  <label class="block text-sm font-medium text-foreground mb-2">Name</label>
                  <Input
                    v-model="inquiry.name"
                    type="text"
                    required
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-foreground mb-2">Phone</label>
                  <Input
                    v-model="inquiry.phone"
                    type="tel"
                    required
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-foreground mb-2">Email</label>
                  <Input
                    v-model="inquiry.email"
                    type="email"
                    required
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-foreground mb-2">Message (Optional)</label>
                  <Textarea
                    v-model="inquiry.message"
                    placeholder="Any specific questions about this vehicle?"
                  />
                </div>
                <Button
                  type="submit"
                  :disabled="submittingInquiry"
                  class="w-full"
                  variant="default"
                >
                  {{ submittingInquiry ? 'Sending...' : 'Send Inquiry' }}
                </Button>
              </form>

              <!-- Trust Signals -->
              <div class="mt-6 space-y-3">
                <div class="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Shield class="w-4 h-4" />
                  <span>Your information is secure</span>
                </div>
                <div class="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Award class="w-4 h-4" />
                  <span>Licensed dealer</span>
                </div>
                <div class="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Star class="w-4 h-4" />
                  <span>4.8/5 customer rating</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>


    <!-- Related Vehicles Section -->
    <section class="py-12">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 class="text-2xl font-bold text-foreground mb-8">
          Similar Vehicles
        </h2>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div 
            v-for="relatedVehicle in relatedVehicles" 
            :key="relatedVehicle.id"
            class="group relative bg-white dark:bg-black rounded-xl overflow-hidden vehicle-card-animation flex flex-col h-full border border-border hover:border-primary transition-colors"
          >
            <!-- Vehicle Image -->
            <div class="relative aspect-16/9">
              <NuxtLink :to="`/vehicle-for-sale/${relatedVehicle.stockid || relatedVehicle.id}/${relatedVehicle.slug || 'details'}`">
                <img 
                  :src="relatedVehicle.image"
                  :alt="relatedVehicle.name" 
                  class="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                >
              </NuxtLink>
              
              <!-- Price Badge -->
              <div class="absolute bottom-0 left-0 right-0 bg-linear-to-t from-black/80 to-transparent p-4">
                <div class="flex items-end justify-between">
                  <div>
                    <div class="text-white font-bold text-xl">
                      {{ formatPrice(relatedVehicle.price) }}
                    </div>
                    <div class="text-white/80 text-sm">
                      {{ formatMileage(relatedVehicle.mileage) }} km
                    </div>
                  </div>
                </div>
              </div>

              <div class="absolute top-2 left-2 flex flex-wrap gap-1">
                <span class="px-2 py-1 bg-black/70 text-white text-xs rounded-full">
                  Used
                </span>
              </div>
            </div>
            
            <!-- Card Content -->
            <div class="flex-1 p-4 flex flex-col">
              <!-- Header -->
              <header class="mb-3">
                <div class="flex items-start justify-between gap-4">
                  <h2 class="text-xl font-bold leading-tight text-slate-900 dark:text-white">
                    {{ relatedVehicle.name }}
                  </h2>
                </div>
              </header>
              
              <!-- Vehicle details -->
              <div class="space-y-4 flex-1">
                <div class="grid grid-cols-2 gap-y-3">
                  <!-- Body Type -->
                  <div class="flex items-center gap-2 text-sm text-gray-600">
                    <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M3 9h18v8a2 2 0 01-2 2H5a2 2 0 01-2-2V9zm0 0l2-4h14l2 4" />
                    </svg>
                    {{ relatedVehicle.body || 'Not specified' }}
                  </div>

                  <!-- Transmission -->
                  <div class="flex items-center gap-2 text-sm text-gray-600">
                    <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M9 6l6 6-6 6" />
                    </svg>
                    {{ relatedVehicle.transmission || 'Not specified' }}
                  </div>

                  <!-- Kilometers -->
                  <div class="flex items-center gap-2 text-sm text-gray-600">
                    <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="8" />
                      <path d="M12 8v4l2 2" />
                    </svg>
                    {{ formatMileage(relatedVehicle.mileage) }} km
                  </div>

                  <!-- Fuel Type -->
                  <div class="flex items-center gap-2 text-sm text-gray-600">
                    <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M19 8C20.6569 8 22 9.34315 22 11V17C22 18.6569 20.6569 20 19 20H16.5C15.9477 20 15.5 19.5523 15.5 19V9C15.5 8.44772 15.9477 8 16.5 8H19Z" />
                      <path d="M6 3C6 2.44772 6.44772 2 7 2H12C12.5523 2 13 2.44772 13 3V22H6V3Z" />
                      <path d="M6 14H13" />
                      <path d="M4 14H5" />
                      <path d="M17 14C17 14 19 11.9948 19 11" />
                    </svg>
                    {{ relatedVehicle.fuel || 'Not specified' }}
                  </div>
                </div>
              </div>
              
              <!-- Actions -->
              <div class="flex flex-wrap items-center gap-2 mt-auto pt-4">
                <NuxtLink 
                  :to="`/vehicle-for-sale/${relatedVehicle.stockid || relatedVehicle.id}/${relatedVehicle.slug || 'details'}`"
                  class="flex-1 inline-flex justify-center items-center whitespace-nowrap rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  View Details
                </NuxtLink>
                <FavoriteButton :vehicle-id="relatedVehicle.id" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Mobile Sticky Action Bar -->
    <div class="lg:hidden fixed bottom-0 left-0 right-0 bg-background border-t border-border p-4 z-50">
      <div class="flex space-x-3">
        <Button
          class="flex-1"
          @click="handleCall"
        >
          <Phone class="w-4 h-4 mr-2" />
          Call Now
        </Button>
        <Button
          class="flex-1"
          variant="secondary"
          @click="handlePrimaryAction"
        >
          <Calendar class="w-4 h-4 mr-2" />
          Test Drive
        </Button>
      </div>
    </div>

    <!-- Dialog Components -->
    <VehicleActionDialog
      v-if="vehicle"
      type="test-drive"
      :vehicle="{
        id: vehicle.id,
        title: vehicle.title || `${vehicleYear} ${vehicleMake} ${vehicleModel} ${vehicleBadge}`.trim(),
        price: vehiclePrice,
        year: vehicleYear,
        make: vehicleMake,
        model: vehicleModel,
        badge: vehicleBadge,
        image: vehicleImages.length > 0 ? vehicleImages[0] : undefined
      }"
      :dealer="{
        name: dealerInfo.name,
        phone: dealerInfo.phone,
        location: dealerInfo.location,
        logo: dealerInfo.logo
      }"
      :is-open="showTestDriveDialog"
      @update:open="showTestDriveDialog = $event"
      @submit="handleDialogSubmit"
    />

    <VehicleActionDialog
      v-if="vehicle"
      type="contact"
      :vehicle="{
        id: vehicle.id,
        title: vehicle.title || `${vehicleYear} ${vehicleMake} ${vehicleModel} ${vehicleBadge}`.trim(),
        price: vehiclePrice,
        year: vehicleYear,
        make: vehicleMake,
        model: vehicleModel,
        badge: vehicleBadge,
        image: vehicleImages.length > 0 ? vehicleImages[0] : undefined
      }"
      :dealer="{
        name: dealerInfo.name,
        phone: dealerInfo.phone,
        location: dealerInfo.location,
        logo: dealerInfo.logo
      }"
      :is-open="showContactDialog"
      @update:open="showContactDialog = $event"
      @submit="handleDialogSubmit"
    />

    <VehicleActionDialog
      v-if="vehicle"
      type="finance"
      :vehicle="{
        id: vehicle.id,
        title: vehicle.title || `${vehicleYear} ${vehicleMake} ${vehicleModel} ${vehicleBadge}`.trim(),
        price: vehiclePrice,
        year: vehicleYear,
        make: vehicleMake,
        model: vehicleModel,
        badge: vehicleBadge,
        image: vehicleImages.length > 0 ? vehicleImages[0] : undefined
      }"
      :dealer="{
        name: dealerInfo.name,
        phone: dealerInfo.phone,
        location: dealerInfo.location,
        logo: dealerInfo.logo
      }"
      :is-open="showFinanceDialog"
      @update:open="showFinanceDialog = $event"
      @submit="handleDialogSubmit"
    />

    <VehicleActionDialog
      v-if="vehicle"
      type="inquiry"
      :vehicle="{
        id: vehicle.id,
        title: vehicle.title || `${vehicleYear} ${vehicleMake} ${vehicleModel} ${vehicleBadge}`.trim(),
        price: vehiclePrice,
        year: vehicleYear,
        make: vehicleMake,
        model: vehicleModel,
        badge: vehicleBadge,
        image: vehicleImages.length > 0 ? vehicleImages[0] : undefined
      }"
      :dealer="{
        name: dealerInfo.name,
        phone: dealerInfo.phone,
        location: dealerInfo.location,
        logo: dealerInfo.logo
      }"
      :is-open="showInquiryDialog"
      @update:open="showInquiryDialog = $event"
      @submit="handleDialogSubmit"
    />

    <!-- AI Chat Dialog -->
    <VehicleAIChatDialog
      v-if="vehicle"
      :vehicle="{
        id: vehicle.id,
        title: vehicle.title || `${vehicleYear} ${vehicleMake} ${vehicleModel} ${vehicleBadge}`.trim(),
        price: vehiclePrice,
        year: vehicleYear,
        make: vehicleMake,
        model: vehicleModel,
        badge: vehicleBadge,
        image: vehicleImages.length > 0 ? vehicleImages[0] : undefined
      }"
      :is-open="showAIChatDialog"
      @update:open="showAIChatDialog = $event"
    />
  </div>
</template>

<style scoped>
/* Custom scrollbar for image thumbnails */
.overflow-x-auto::-webkit-scrollbar {
  height: 4px;
}

.overflow-x-auto::-webkit-scrollbar-track {
  background: hsl(var(--muted));
  border-radius: 2px;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
  background: hsl(var(--muted-foreground));
  border-radius: 2px;
}

.overflow-x-auto::-webkit-scrollbar-thumb:hover {
  background: hsl(var(--foreground));
}
</style>
